/* sts-liquid-quiz.js — Liquid Quiz PoC engine (liquid-proof-of-concept)
 *
 * Vanilla ES2019, classic script (defer), no build step, no imports.
 * Spec: skin-type-pro/docs/superpowers/specs/2026-08-06-liquid-quiz-poc.md
 *
 * Owns: view state machine (landing/quiz/calculating/results), questions
 * fetch + rendering (all 4 types), conditional visibility, validation,
 * email gate (+honeypot, +optional Turnstile), submit with the widget's
 * exact retry budgets and byte-identical body, outcome handling, the
 * localStorage allow-list write, per-type CSS custom properties, and the
 * window.SLQ.track telemetry helper.
 *
 * window.SLQ contract (shared with sts-liquid-quiz-results.js):
 *   SLQ.track(event, detail)       — telemetry (auto-includes widgetToken)
 *   SLQ.state                      — {widgetToken, skinType, sensitivityLevel, ...} after success
 *   SLQ.settings                   — {nextBase, ec2Base, proxyBase, useProxySubmit, turnstileSiteKey}
 *   SLQ.go(view)                   — switch view shells
 *   SLQ.toast(msg)                 — prototype toast
 *   SLQ.brighten(hex)              — prototype :871 hex lighten (per-type theming)
 *   SLQ.typeInfo(code)             — entry from window.__STS_SKIN_TYPE_INFO__ or null
 *   SLQ.results.show(state)        — provided by the RESULTS file; if absent when
 *                                    success lands, SLQ.pendingResult is set and the
 *                                    results file must consume it on load.
 *
 * No secrets, no Lambda/API-Gateway URLs, no PII in telemetry or URLs.
 */
(function () {
  'use strict';

  var SLQ = (window.SLQ = window.SLQ || {});

  var WIDGET_VERSION = 'liquid-poc-1';
  var PAGE_CONTEXT = 'quiz-page-liquid';
  var ASSIGNMENTS = { concept: 'liquid-poc' };

  // Retry budgets — copied from the headless widget (spec §1.3/§5)
  var NETWORK_MAX_RETRIES = 2;
  var NETWORK_RETRY_DELAY_MS = 1000;
  var IN_FLIGHT_MAX_ATTEMPTS = 24;
  var DETERMINISTIC_NO_RETRY_STATUSES = [400, 401, 403];
  var CALC_MIN_DISPLAY_MS = 2500;
  var CALC_STAGE_MS = 950; // prototype startCalc() cadence

  // The display mode option buttons are built with (renderQuestion ~:728).
  // Anything that re-shows a hidden option MUST restore this, never '' — see
  // applyAffiliateOptionRules.
  var OPT_DISPLAY = 'flex';

  var AXES = { D: 'Dry', O: 'Oily', S: 'Sensitive', R: 'Resistant', P: 'Pigmented', N: 'Non-Pigmented', W: 'Wrinkle-Prone', T: 'Tight' };
  var NEUTRAL_HEX = '#7B85B4';

  // ---------------------------------------------------------------- utilities

  var root = null;

  function $(sel, scope) { return (scope || root).querySelector(sel); }
  function $all(sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // RFC4122 v4 via getRandomValues (older Safari)
    var bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < 16; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
    return hex.slice(0, 4).join('') + '-' + hex.slice(4, 6).join('') + '-' + hex.slice(6, 8).join('') + '-' + hex.slice(8, 10).join('') + '-' + hex.slice(10, 16).join('');
  }

  function ssGet(key) { try { return window.sessionStorage.getItem(key); } catch (e) { return null; } }
  function ssSet(key, val) { try { window.sessionStorage.setItem(key, val); } catch (e) { /* private mode */ } }
  function lsGet(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function lsSet(key, val) { try { window.localStorage.setItem(key, val); } catch (e) { /* private mode */ } }

  function delay(ms) { return new Promise(function (resolve) { window.setTimeout(resolve, ms); }); }

  function stripTrailingSlash(s) { return String(s || '').replace(/\/+$/, ''); }

  // ------------------------------------------------------------------ session

  function sessionUuid() {
    var existing = ssGet('slq_session_uuid');
    if (existing) return existing;
    var fresh = uuid();
    ssSet('slq_session_uuid', fresh);
    return fresh;
  }

  function nextClientSeq() {
    var raw = parseInt(ssGet('slq_client_seq') || '0', 10);
    if (!isFinite(raw) || raw < 0) raw = 0;
    var next = raw + 1;
    ssSet('slq_client_seq', String(next));
    return next;
  }

  // ------------------------------------------------------ Google Analytics
  // The Octane quiz pushed sts_quiz_visited / sts_quiz_started / sts_quiz_completed
  // (event_category "Custom Event") into the store's Google tag, and layout/theme.liquid added
  // quiz_question_loaded / quiz_question_answered on top of Octane's DOM
  // events. Both went through window.gtag -> G-4BBMCNMW2M (+ AW-412659181).
  // This engine re-points them: same transport (gtag, falling back to a
  // dataLayer push when gtag is not on the page), same Northbeam mirror,
  // names configurable per section so GA4 key events can be matched without a
  // deploy. Never sends PII: free-text answers (name, email, ZIP, birth date)
  // are never included - only option values of choice questions.
  function gaEvent(key, params) {
    try {
      var ga = SLQ.settings && SLQ.settings.ga;
      var name = ga && ga[key];
      if (!name) return;
      var payload = { event_category: ga.category || 'Custom Event' };
      if (params && typeof params === 'object') {
        Object.keys(params).forEach(function (k) {
          if (params[k] !== undefined && params[k] !== null && params[k] !== '') payload[k] = params[k];
        });
      }
      gaSend(name, payload);
      // Meta: Octane's pixel integration sent custom events for quiz viewed/
      // started/completed into the store pixel (766412540681903, loaded by
      // Shopify's Facebook & Instagram channel). Mirror those three milestones
      // with the same names; per-question events stay GA-only, matching
      // Octane's Meta scope.
      if (key === 'landed' || key === 'started' || key === 'completed') {
        metaSend(name, payload);
      }
      if (window.Northbeam && typeof window.Northbeam.fireCustomGoal === 'function') {
        window.Northbeam.fireCustomGoal(name, {});
      }
    } catch (e) { /* analytics must never break the quiz */ }
  }
  SLQ.gaEvent = gaEvent;

  // NEVER hand events to the fbq STUB. This store's lazy-load optimizer
  // (snippets/social-meta-tags.liquid) re-injects fbevents.js TWICE, and both
  // copies replay the stub's queue — measured live 2026-08-20: every event
  // queued pre-load hit /tr twice, while post-load calls hit once. So wait for
  // the REAL script (fbq.getState only exists once fbevents.js has run) and
  // only ever call the loaded pixel; our own queue holds events until then.
  var metaQueue = [];
  var metaWaiter = null;
  var metaWaitedMs = 0;
  function metaReady() {
    return typeof window.fbq === 'function' && typeof window.fbq.getState === 'function';
  }
  function metaSend(name, payload) {
    // eventID makes the send idempotent: the optimizer's second fbevents.js
    // copy replays calls it did not originate, but the replay carries the
    // same eventID, and Meta deduplicates on (event name, eventID).
    var eid = 'slq-' + uuid();
    if (metaReady()) {
      window.fbq('trackCustom', name, payload, { eventID: eid });
      return;
    }
    metaQueue.push({ name: name, payload: payload, eid: eid });
    if (metaWaiter) return;
    metaWaiter = window.setInterval(function () {
      metaWaitedMs += 250;
      var ready = metaReady();
      if (!ready && metaWaitedMs < 20000) return;
      window.clearInterval(metaWaiter);
      metaWaiter = null;
      var q = metaQueue.splice(0);
      if (!ready) return; // no pixel on this page (blocked/consent) — drop quietly
      q.forEach(function (item) { window.fbq('trackCustom', item.name, item.payload, { eventID: item.eid }); });
    }, 250);
  }

  // window.gtag is defined by Shopify's Google & YouTube channel snippet, which
  // loads async — on a fast page the quiz inits (DOMContentLoaded) BEFORE it
  // exists, and the very first event (quiz visited) would be lost. So: send
  // now when gtag is there, otherwise hold the event and flush the moment it
  // appears (checked 4x/s for 20 s). If it never shows up (blocked), fall back
  // to a plain dataLayer push so a GTM trigger can still pick it up.
  var gaQueue = [];
  var gaWaiter = null;
  var gaWaitedMs = 0;
  function gaSend(name, payload) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
      return;
    }
    gaQueue.push({ name: name, payload: payload });
    if (gaWaiter) return;
    gaWaiter = window.setInterval(function () {
      gaWaitedMs += 250;
      var ready = typeof window.gtag === 'function';
      if (!ready && gaWaitedMs < 20000) return;
      window.clearInterval(gaWaiter);
      gaWaiter = null;
      var q = gaQueue.splice(0);
      q.forEach(function (item) {
        if (ready) {
          window.gtag('event', item.name, item.payload);
        } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          var dl = { event: item.name };
          Object.keys(item.payload).forEach(function (k) { dl[k] = item.payload[k]; });
          window.dataLayer.push(dl);
        }
      });
    }, 250);
  }

  function gaQuestionFields(q) {
    return {
      question_id: q && q.id,
      question_title: q && q.text ? String(q.text).slice(0, 100) : undefined,
      question_type: q && q.type
    };
  }

  // ---------------------------------------------------------------- telemetry
  // Spec §7: envelope {token?, events:[...]}, POST {ec2_base}/widget/events.
  // Required per event: event_uuid, session_uuid, client_seq (monotonic int,
  // persisted in sessionStorage), occurred_at (ISO), event. No PII ever.

  SLQ.track = function track(eventName, detail) {
    try {
      if (!SLQ.settings || !SLQ.settings.ec2Base) return;
      var evt = {
        event_uuid: uuid(),
        session_uuid: sessionUuid(),
        client_seq: nextClientSeq(),
        occurred_at: new Date().toISOString(),
        event: String(eventName),
        widget_version: WIDGET_VERSION,
        page_context: PAGE_CONTEXT,
        assignments: ASSIGNMENTS,
        device_class: window.innerWidth <= 760 ? 'mobile' : 'desktop'
      };
      if (typeof state.configVersionInt === 'number') evt.config_version = state.configVersionInt;
      if (detail && typeof detail === 'object') {
        // question_code/step_index are TOP-LEVEL event columns (migration 212;
        // the weekly evidence pack groups on question_code) — hoist them out
        // of detail so the server actually stores them.
        if (typeof detail.question_code === 'string') { evt.question_code = detail.question_code; }
        if (typeof detail.step_index === 'number') { evt.step_index = detail.step_index; }
        evt.detail = detail;
      }
      var envelope = { events: [evt] };
      if (SLQ.state && SLQ.state.widgetToken) envelope.token = SLQ.state.widgetToken;
      var url = stripTrailingSlash(SLQ.settings.ec2Base) + '/widget/events';
      var payload = JSON.stringify(envelope);

      // sendBeacon FIRST: add_to_cart fires immediately before the cart
      // navigation, and fetch+keepalive only *sometimes* survives that unload
      // (observed: cart-add events intermittently missing server-side). Beacon
      // is queued by the browser and delivered regardless of navigation. The
      // events endpoint accepts text/plain exactly for this path.
      try {
        if (navigator.sendBeacon) {
          var blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
          if (navigator.sendBeacon(url, blob)) return;
        }
      } catch (e) { /* fall through to fetch */ }

      window.fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(function () { /* telemetry is fire-and-forget */ });
    } catch (e) { /* never break the quiz for telemetry */ }
  };

  // ---------------------------------------------- per-type theming (spec §3)

  // Port of prototype brighten() (:871) — hex -> HSL lighten to l=.72
  function brighten(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    if (l >= 0.68) return hex;
    return hslToHex(h, s, 0.72);
  }

  function hslToHex(h, s, l) {
    var hue2rgb = function (p, q, t) {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    var p2 = 2 * l - q;
    var to = function (x) { return Math.round(hue2rgb(p2, q, x) * 255).toString(16).padStart(2, '0'); };
    return '#' + to(h + 1 / 3) + to(h) + to(h - 1 / 3);
  }

  function hexToHsl(hex) {
    var r = parseInt(hex.slice(1, 3), 16) / 255;
    var g = parseInt(hex.slice(3, 5), 16) / 255;
    var b = parseInt(hex.slice(5, 7), 16) / 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
      else if (max === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h /= 6;
    }
    return { h: h, s: s, l: l };
  }

  SLQ.brighten = brighten;

  SLQ.typeInfo = function typeInfo(code) {
    if (!code) return null;
    var info = window.__STS_SKIN_TYPE_INFO__;
    if (!info || typeof info !== 'object') return null;
    return info[code] || info[String(code).toUpperCase()] || null;
  };

  // Sets the slq- custom properties on BOTH documentElement (contract for the
  // results file) and the section root (wins over any stylesheet default).
  // deep/tint are presentation shades derived from the type's real color.
  function setTypeColors(hex) {
    if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    var hsl = hexToHsl(hex);
    var deep = hslToHex(hsl.h, hsl.s, Math.max(0.18, hsl.l - 0.14));
    var tint = hslToHex(hsl.h, Math.min(1, hsl.s), Math.min(0.94, Math.max(hsl.l, 0.88)));
    var bright = brighten(hex);
    [document.documentElement, root].forEach(function (el) {
      if (!el) return;
      // both spellings: short names (inline styles in the section/snippet
      // markup + results engine) and long-form names (sts-liquid-quiz.css)
      el.style.setProperty('--slq-type', hex);
      el.style.setProperty('--slq-type-color', hex);
      el.style.setProperty('--slq-type-bright', bright);
      el.style.setProperty('--slq-type-color-bright', bright);
      el.style.setProperty('--slq-type-deep', deep);
      el.style.setProperty('--slq-type-color-deep', deep);
      el.style.setProperty('--slq-type-tint', tint);
      el.style.setProperty('--slq-type-color-tint', tint);
    });
  }

  // ------------------------------------- conditional visibility (verbatim port
  // of lib/quiz-core/index.ts:5-102)

  function evaluateRule(rule, answers) {
    var answer = answers[rule.target_question_code];
    if (!answer) return false;
    var answerValue = Array.isArray(answer) ? answer : [answer];
    switch (rule.operator) {
      case 'equals':
        return answerValue.indexOf(rule.value_code) !== -1;
      case 'not_equals':
        return answerValue.indexOf(rule.value_code) === -1;
      case 'contains':
        return answerValue.some(function (val) { return String(val).indexOf(rule.value_code) !== -1; });
      case 'not_contains':
        return !answerValue.some(function (val) { return String(val).indexOf(rule.value_code) !== -1; });
      case 'includes':
        return answerValue.indexOf(rule.value_code) !== -1;
      case 'excludes':
        return answerValue.indexOf(rule.value_code) === -1;
      default:
        return false;
    }
  }

  function evaluateQuestionConditions(question, answers) {
    if (!question.conditions || question.conditions.length === 0) return true;
    for (var i = 0; i < question.conditions.length; i++) {
      var condition = question.conditions[i];
      var results = condition.rules.map(function (rule) { return evaluateRule(rule, answers); });
      var conditionMet = condition.type === 'OR'
        ? results.some(function (r) { return r; })
        : results.every(function (r) { return r; });
      if (!conditionMet) return false;
    }
    return true;
  }

  function getVisibleQuestions(allQuestions, answers) {
    return allQuestions.filter(function (q) { return evaluateQuestionConditions(q, answers); });
  }

  function getNextVisibleQuestion(allQuestions, currentIndex, answers, direction) {
    var increment = direction === 'previous' ? -1 : 1;
    var nextIndex = currentIndex + increment;
    while (nextIndex >= 0 && nextIndex < allQuestions.length) {
      if (evaluateQuestionConditions(allQuestions[nextIndex], answers)) return nextIndex;
      nextIndex += increment;
    }
    return -1;
  }

  // ------------------------------------------- validation ports (payload.ts +
  // widget Question.tsx isValidMonthYear)

  // lib/quiz-core/payload.ts:23-40 — returns null when valid, else message
  function validateName(raw) {
    var trimmed = String(raw || '').trim();
    if (!trimmed) return 'Please enter your full name.';
    if (trimmed.length > 80) return 'Name is too long.';
    if (/[@/]|https?:|www\.|\d/.test(trimmed)) {
      return 'Please enter a valid name (letters only).';
    }
    var parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return 'Please enter both your first and last name.';
    var NAME_PART_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ][A-Za-zÀ-ÖØ-öø-ÿ'\-.]{1,}$/;
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p.length < 2) return 'Each part of your name must be at least 2 letters.';
      if (!NAME_PART_RE.test(p)) return 'Please enter a valid name (letters, hyphens, apostrophes only).';
    }
    return null;
  }

  // lib/quiz-core/payload.ts:48-66
  function validateAnswer(question, answer) {
    if (!question) return false;
    if (question.id === 'q17') {
      var value = Array.isArray(answer) ? (answer[0] || '') : (answer || '');
      return validateName(value) === null;
    }
    if (question.required) {
      if (Array.isArray(answer)) return answer.length > 0;
      return typeof answer === 'string' && answer.trim().length > 0;
    }
    return true;
  }

  // Index (into state.questions) of the first CURRENTLY VISIBLE question that
  // has no valid answer, or -1 when the set is complete.
  //
  // Forward play cannot leave a hole — every Continue runs validateAnswer — so
  // the only way to reach the gate with a partial set is a restored checkpoint
  // whose currentIndex outruns its answers. sessionStorage is editable, and a
  // questionnaire edit can shift the list under a saved index (the `version`
  // guard is inert while the API reports the constant "default"). A partial
  // set scores as a 500, which the shopper cannot recover from — the retry
  // button re-sends the same partial set — so both the resume path and submit
  // check this. Visibility is evaluated against the CURRENT answers so a
  // conditional question that does not apply (e.g. pregnancy for a male
  // respondent) can never block a legitimate submission.
  function firstIncompleteQuestionIndex() {
    var visible = getVisibleQuestions(state.questions, state.answers);
    for (var i = 0; i < visible.length; i++) {
      if (!validateAnswer(visible[i], state.answers[visible[i].id])) {
        return state.questions.indexOf(visible[i]);
      }
    }
    return -1;
  }

  /* Turn whatever the visitor has typed into MM/YYYY as they go, so they never
     have to type the "/" themselves — a numeric keyboard has no slash key,
     which made this question unanswerable on a phone.

       "2"    -> "02/"     a first digit above 1 can only be a single-digit
                           month, so pad it and move straight to the year
       "1"    -> "1"       still ambiguous (1, 10, 11, 12) — wait for the next
       "04"   -> "04/"     month complete, slash inserted
       "041985" -> "04/1985"

     `deleting` suppresses the auto-slash during a backspace: re-appending it
     would restore the character the visitor is trying to remove and trap the
     caret at the slash forever. */
  function formatMonthYear(raw, deleting) {
    var d = String(raw == null ? '' : raw).replace(/\D/g, '').slice(0, 6);
    if (d.length === 1 && d > '1') d = '0' + d;
    if (d.length <= 2) return (d.length === 2 && !deleting) ? d + '/' : d;
    return d.slice(0, 2) + '/' + d.slice(2);
  }

  // widget Question.tsx:151-158 — MM/YYYY, month 01-12, year 1920..current
  function isValidMonthYear(value) {
    var match = /^(\d{2})\/(\d{4})$/.exec(value);
    if (!match) return false;
    var month = parseInt(match[1], 10);
    var year = parseInt(match[2], 10);
    var currentYear = new Date().getFullYear();
    return month >= 1 && month <= 12 && year >= 1920 && year <= currentYear;
  }

  // --------------------------------------------- payload ports (submit.ts
  // buildSubmitRequestBody :255-314 non-guest path + its helpers)

  // submit.ts:231-245 — drop blank optional answers at the wire boundary only
  function omitBlankOptionalAnswers(answers, questions) {
    var effective = {};
    Object.keys(answers).forEach(function (questionId) {
      var value = answers[questionId];
      var question = null;
      for (var i = 0; i < questions.length; i++) {
        if (questions[i].id === questionId) { question = questions[i]; break; }
      }
      var isBlank = Array.isArray(value) ? value.length === 0 : value === '';
      if (question && question.required === false && isBlank) return;
      effective[questionId] = value;
    });
    return effective;
  }

  // lib/quiz-core/payload.ts:76-90
  function buildFormattedAnswers(answers, questions) {
    return Object.keys(answers).map(function (questionId) {
      var answer = answers[questionId];
      var question = null;
      for (var i = 0; i < questions.length; i++) {
        if (questions[i].id === questionId) { question = questions[i]; break; }
      }
      var category = (question && question.category) || 'demographics';
      return {
        questionId: questionId,
        answer: Array.isArray(answer) ? answer.join(', ') : answer,
        category: category,
        weight: 1
      };
    });
  }

  // lib/quiz-core/user-info.ts:45-89 (verbatim behavior)
  function deriveUserInfoFromAnswers(answers) {
    var derived = {};
    if ('q14' in answers) {
      var g = answers.q14;
      derived.gender = Array.isArray(g) ? g[0] : g;
    }
    if ('q16' in answers) {
      var e = answers.q16;
      derived.ethnicity = Array.isArray(e) ? e.join(', ') : e;
    }
    if ('q17' in answers) {
      var n = answers.q17;
      var names = (Array.isArray(n) ? n[0] : n).split(' ');
      derived.firstName = names[0] || '';
      derived.lastName = names.slice(1).join(' ') || '';
    }
    if ('q18' in answers) {
      var d = answers.q18;
      var pieces = (Array.isArray(d) ? d[0] : d).split('/');
      derived.birthMonth = pieces[0] || '';
      derived.birthYear = pieces[1] || '';
    }
    if ('q19' in answers) {
      var z = answers.q19;
      derived.zipCode = Array.isArray(z) ? z[0] : z;
    }
    return derived;
  }

  // submit.ts buildSubmitRequestBody (non-guest) — spec §1.3/§5 body shape
  function buildSubmitRequestBody(submitState) {
    var effectiveAnswers = omitBlankOptionalAnswers(submitState.answers, submitState.questions);
    var formattedAnswers = buildFormattedAnswers(effectiveAnswers, submitState.questions);
    var derived = deriveUserInfoFromAnswers(effectiveAnswers);

    var userInfo = {};
    Object.keys(derived).forEach(function (k) { userInfo[k] = derived[k]; });
    userInfo.firstName = derived.firstName || '';
    userInfo.lastName = derived.lastName || '';
    userInfo.email = submitState.email;
    userInfo.marketingConsent = submitState.marketingConsent;
    // Precedence mirrors legacy assets/quiz.js: typed practice code (q1) wins,
    // then URL/stored code, then the storewide DTC default 'STSMD' — the
    // Lambda hard-requires an affiliateCode on every submission (400 otherwise).
    var q1 = submitState.answers && submitState.answers.q1;
    var typedCode = (Array.isArray(q1) ? q1[0] : q1 || '').trim().toUpperCase();
    userInfo.affiliateCode = typedCode || submitState.affiliateCode || 'STSMD';
    // Shopify's market country for the shopper (optional; backend stores it as market_country).
    var mcRoot = document.querySelector('[data-slq-root]');
    var mc = String((mcRoot && mcRoot.getAttribute('data-market-country')) || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(mc)) userInfo.marketCountry = mc;

    var body = {
      answers: formattedAnswers,
      userInfo: userInfo
    };
    // truthy-only spreads, matching buildSubmitPayload exactly (empty string
    // honeypot / unset Turnstile token must be OMITTED, not falsy-present)
    if (submitState.turnstileToken) body.turnstileToken = submitState.turnstileToken;
    if (submitState.honeypot) body._hp = submitState.honeypot;
    body.context = 'widget';
    body.submissionId = submitState.submissionId;
    body.session_uuid = submitState.sessionUuid;
    body.assignments = ASSIGNMENTS;
    body.configVersion = submitState.configVersion;
    return body;
  }

  // ---------------------------------------------------------------- app state

  var state = {
    view: 'landing',
    questions: [],
    questionsVersion: null,
    configVersionInt: 0,
    questionsStatus: 'idle', // idle | loading | ready | error
    // Where the question COPY came from, read off the payload envelope:
    //   'overlay' = the content service answered; published copy is merged in
    //   'static'  = the API could not reach it and served the shipped copy
    //   null      = this API build predates STS-Data and says nothing either way
    contentSource: null,
    contentRevision: null,
    whyWeAskServed: 0,
    answers: {},
    currentIndex: -1,
    draft: null,            // working value for the current question (string or string[])
    trackedQuizStart: false,
    affiliateCode: null,
    q1Locked: false,       // q1 is showing the hidden 'code applied' confirmation, not an editable box
    email: '',
    marketingConsent: false,
    submitting: false,
    calcTimers: [],
    calcStartedAt: 0,
    calcStage: 0
  };

  // -------------------------------------------------------------- view switch

  // Fullscreen takeover: quiz/calculating/results hide EVERYTHING outside the
  // section (header, menus, footer, announcement bars, third-party popups) and
  // show only the fixed X. Implemented by hiding every sibling on the path
  // from the section root up to <body>, remembering each element's inline
  // display so exiting restores the page exactly. The X returns to landing.
  var chromeHidden = [];
  var takeoverObserver = null;

  // Third-party popups (Klaviyo forms, consent bars…) inject themselves as
  // new <body> children AFTER the takeover snapshot ran — watch for them and
  // hide them too while the takeover is active.
  function watchLateChrome() {
    if (takeoverObserver || typeof MutationObserver === 'undefined') return;
    takeoverObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        for (var i = 0; i < m.addedNodes.length; i++) {
          var n = m.addedNodes[i];
          if (n.nodeType !== 1) continue;
          var tag = n.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
          if (n.hasAttribute('data-slq-keep') || n.hasAttribute('data-slq-chrome-hidden')) continue;
          if (n.contains(root) || n === root) continue;
          n.setAttribute('data-slq-chrome-hidden', n.style.display || '');
          n.style.display = 'none';
          chromeHidden.push(n);
        }
      });
    });
    takeoverObserver.observe(document.body, { childList: true });
  }

  function unwatchLateChrome() {
    if (takeoverObserver) { takeoverObserver.disconnect(); takeoverObserver = null; }
  }

  function setTakeover(on) {
    var exitBtn = root ? root.querySelector('[data-slq-exit]') : null;
    if (exitBtn) exitBtn.toggleAttribute('hidden', !on);
    if (on) {
      if (chromeHidden.length) { watchLateChrome(); return; } // already on
      watchLateChrome();
      document.body.setAttribute('data-slq-takeover', '');
      var node = root;
      while (node && node.parentElement && node.tagName !== 'BODY') {
        var parent = node.parentElement;
        for (var i = 0; i < parent.children.length; i++) {
          var sib = parent.children[i];
          if (sib === node) continue;
          var tag = sib.tagName;
          if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') continue;
          if (sib.hasAttribute('data-slq-keep') || sib.hasAttribute('data-slq-chrome-hidden')) continue;
          sib.setAttribute('data-slq-chrome-hidden', sib.style.display || '');
          sib.style.display = 'none';
          chromeHidden.push(sib);
        }
        node = parent;
      }
    } else {
      unwatchLateChrome();
      document.body.removeAttribute('data-slq-takeover');
      chromeHidden.forEach(function (el) {
        el.style.display = el.getAttribute('data-slq-chrome-hidden') || '';
        el.removeAttribute('data-slq-chrome-hidden');
      });
      chromeHidden = [];
    }
  }

  function go(view) {
    state.view = view;
    $all('[data-slq-view]').forEach(function (el) {
      if (el.getAttribute('data-slq-view') === view) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
    setTakeover(view !== 'landing');
    window.scrollTo({ top: 0 });
  }
  SLQ.go = go;

  // -------------------------------------------------------------------- toast

  SLQ.toast = function toast(msg) {
    var host = $('[data-slq-toasts]');
    if (!host) return;
    var el = document.createElement('div');
    el.style.cssText = 'background:#2F3572;color:#fff;font-size:13.5px;font-weight:500;padding:13px 22px;border-radius:999px;box-shadow:0 12px 30px rgba(15,18,50,.35);display:flex;align-items:center;gap:10px;animation:slq-toastIn .3s ease';
    var dot = document.createElement('span');
    dot.style.cssText = 'width:8px;height:8px;clip-path:polygon(50% 0%,67% 9%,85% 15%,91% 33%,100% 50%,91% 67%,85% 85%,67% 91%,50% 100%,33% 91%,15% 85%,9% 67%,0% 50%,9% 33%,15% 15%,33% 9%);background:var(--slq-type-color-bright);flex:none';
    el.appendChild(dot);
    el.appendChild(document.createTextNode(msg));
    host.appendChild(el);
    window.setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 3400);
  };

  // ---------------------------------------------------------------- questions

  // The storewide DTC default. It is not a real practice code — it is what
  // every visitor who arrives without one gets, including via the site-wide
  // `affiliateCode` localStorage key that persists with no TTL. Rendering q1
  // for it produces a Continue-only dead click, so it is treated exactly like
  // "no code": q1 is dropped and submit applies the default anyway.
  var HOUSE_AFFILIATE_CODE = 'STSMD';

  // Single source of truth for whether q1 is part of this run's question set.
  // saveProgress/restoreProgress key off the SAME predicate, otherwise a
  // session saved on the real-code branch could restore onto the STSMD branch
  // (both have a truthy affiliateCode) and land currentIndex on the wrong
  // question.
  function showsPracticeCodeQuestion() {
    var code = (state.affiliateCode || '').trim();
    return !!code && code.toUpperCase() !== HOUSE_AFFILIATE_CODE;
  }

  function fetchQuestions() {
    if (state.questionsStatus === 'loading' || state.questionsStatus === 'ready') return Promise.resolve();
    state.questionsStatus = 'loading';
    renderQuizStatus();
    var url = stripTrailingSlash(SLQ.settings.nextBase) + '/api/quiz/questions';
    return window.fetch(url, { method: 'GET' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.questions) || data.questions.length === 0) {
          throw new Error('empty questions payload');
        }
        state.questions = data.questions;
        // No code, or only the house default -> the STSMD path: the Practice
        // Code question is skipped entirely (submit already defaults to STSMD).
        // A real practice code still gets its confirmation screen.
        if (!showsPracticeCodeQuestion()) {
          state.questions = state.questions.filter(function (q) { return q.id !== 'q1'; });
        }
        state.questionsVersion = data.version;
        // configVersion: numeric version, else 0 (version "default" -> 0)
        var v = parseInt(data.version, 10);
        state.configVersionInt = isFinite(v) ? v : 0;
        // Dr. Baumann's published copy rides THIS payload — there is no second
        // request. Titles, help text and option labels are merged in place by
        // the API; the "why we ask this question" paragraphs arrive as an
        // additive per-question `whyWeAsk` array that renderQuestionInfo()
        // prefers over the QUESTION_INFO fallback below. These envelope fields
        // are what make the source observable at runtime instead of a guess:
        // 'static' means the copy on screen is the SHIPPED copy because the
        // content service was unreachable, which is not the same thing as
        // nothing having been published.
        state.contentSource = typeof data.contentSource === 'string' ? data.contentSource : null;
        state.contentRevision = typeof data.contentRevision === 'number' ? data.contentRevision : null;
        state.whyWeAskServed = 0;
        for (var wi = 0; wi < state.questions.length; wi++) {
          // `!== null` counts a deliberately-emptied explanation as served,
          // because it is: the storefront is showing her decision, not the
          // theme's fallback.
          if (servedWhyWeAsk(state.questions[wi]) !== null) state.whyWeAskServed++;
        }
        if (root) {
          root.setAttribute('data-slq-content-source', state.contentSource || 'unknown');
          root.setAttribute('data-slq-content-revision',
            state.contentRevision === null ? '' : String(state.contentRevision));
          root.setAttribute('data-slq-why-served', String(state.whyWeAskServed));
        }
        try {
          console.info('[SLQ] question copy: source=' + (state.contentSource || 'unknown')
            + ' revision=' + (state.contentRevision === null ? 'n/a' : state.contentRevision)
            + ' - why-we-ask served for ' + state.whyWeAskServed + ' of '
            + state.questions.length + ' question(s); the rest use the theme fallback.');
        } catch (e) { /* logging is advisory only */ }
        state.questionsStatus = 'ready';
        restoreProgress();
        if (state.view === 'quiz') startQuestionFlow();
      })
      .catch(function (err) {
        state.questionsStatus = 'error';
        state.questionsError = (err && err.message) ? err.message : 'network error';
        renderQuizStatus();
      });
  }

  // ------------------------------------------------------- progress persist
  // Mid-quiz reloads must never lose answers: every commit checkpoints
  // {answers, currentIndex} to sessionStorage (per-tab). Cleared on a
  // successful submit; a version mismatch discards stale progress.
  var PROGRESS_KEY = 'slqQuizProgress';

  function saveProgress() {
    try {
      sessionStorage.setItem(PROGRESS_KEY, JSON.stringify({
        answers: state.answers,
        currentIndex: state.currentIndex,
        version: state.questionsVersion,
        hadCode: showsPracticeCodeQuestion()
      }));
    } catch (e) { /* noop */ }
  }

  function clearProgress() {
    try { sessionStorage.removeItem(PROGRESS_KEY); } catch (e) { /* noop */ }
  }

  function restoreProgress() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(PROGRESS_KEY) || 'null');
      if (!saved || !saved.answers || saved.version !== state.questionsVersion) return false;
      if (saved.hadCode !== showsPracticeCodeQuestion()) return false; // q1 presence differs
      state.answers = saved.answers;
      var idx = parseInt(saved.currentIndex, 10);
      if (isFinite(idx) && idx >= 0 && idx < state.questions.length) state.currentIndex = idx;
      // Never resume PAST an unanswered question. A checkpoint whose index
      // outruns its answers would drop the visitor near the end, let them walk
      // to the gate, and submit a partial set. Rewind to the gap instead of
      // discarding the whole checkpoint — their real answers are still good.
      var gap = firstIncompleteQuestionIndex();
      if (gap !== -1 && state.currentIndex > gap) state.currentIndex = gap;
      return Object.keys(saved.answers).length > 0;
    } catch (e) { return false; }
  }

  function hasSavedProgress() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(PROGRESS_KEY) || 'null');
      return !!(saved && saved.answers && Object.keys(saved.answers).length > 0);
    } catch (e) { return false; }
  }

  function renderQuizStatus() {
    var loading = $('[data-slq-quiz-loading]');
    var error = $('[data-slq-quiz-error]');
    var main = $('[data-slq-quiz-main]');
    if (!loading || !error || !main) return;
    loading.toggleAttribute('hidden', state.questionsStatus !== 'loading');
    error.toggleAttribute('hidden', state.questionsStatus !== 'error');
    main.toggleAttribute('hidden', state.questionsStatus !== 'ready');
    if (state.questionsStatus === 'error') {
      var msg = $('[data-slq-quiz-error-msg]');
      if (msg) msg.textContent = 'The questionnaire could not be fetched (' + (state.questionsError || 'network error') + '). Check your connection and try again.';
    }
  }

  // ------------------------------------------------------------- quiz engine

  function startQuiz() {
    go('quiz');
    if (state.questionsStatus === 'ready') {
      startQuestionFlow();
    } else {
      fetchQuestions();
    }
  }

  function startQuestionFlow() {
    renderQuizStatus();
    if (state.currentIndex === -1) {
      var first = getNextVisibleQuestion(state.questions, -1, state.answers, 'next');
      state.currentIndex = first;
    }
    // "started" goes out BEFORE the first question's quiz_question_loaded so
    // GA4 funnels read visited -> started -> question -> ... -> completed.
    if (!state.trackedQuizStart) gaEvent('started');
    renderQuestion();
    if (!state.trackedQuizStart) {
      state.trackedQuizStart = true;
      SLQ.track('quiz_start', {
        content_source: state.contentSource || 'unknown',
        content_revision: state.contentRevision,
        why_we_ask_served: state.whyWeAskServed
      });
    }
  }

  function currentQuestion() {
    return state.questions[state.currentIndex] || null;
  }

  function renderQuestion() {
    var q = currentQuestion();
    if (!q) return;
    state.draft = ('' + q.id) in state.answers ? state.answers[q.id] : (q.type === 'multiple-choice' ? [] : '');

    // progress: position within currently-visible questions
    var visible = getVisibleQuestions(state.questions, state.answers);
    var pos = 0;
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].id === q.id) { pos = i + 1; break; }
    }
    var bar = $('[data-slq-progress-bar]');
    if (bar) bar.style.width = Math.round((Math.max(pos - 1, 0) / Math.max(visible.length, 1)) * 100) + '%';
    var count = $('[data-slq-progress-count]');
    if (count) count.textContent = 'Question ' + pos + ' of ' + visible.length;

    var qtext = $('[data-slq-qtext]');
    if (qtext) qtext.textContent = q.text || '';
    gaEvent('questionLoaded', gaQuestionFields(q));
    // internal telemetry twin of the GA event: code only, never answer text —
    // feeds the evidence pack's per-question completion digest
    SLQ.track('question_viewed', { question_code: q.id, question_type: q.type });
    var qhelp = $('[data-slq-qhelp]');
    var hasHelp = false;
    if (qhelp) {
      if (q.helpText) { qhelp.textContent = q.helpText; qhelp.removeAttribute('hidden'); hasHelp = true; }
      else { qhelp.textContent = ''; qhelp.setAttribute('hidden', ''); }
    }
    hideError();
    renderQuestionInfo(q);

    var body = $('[data-slq-qbody]');
    if (!body) return;
    // Name the option group after the question, plus the help text when it is
    // showing — that is where "check all that are true" / "choose one" lives, so
    // without it a screen-reader user is never told the question is multi-select.
    // A hidden element must NOT stay in aria-labelledby or it is read anyway.
    body.setAttribute('aria-labelledby', hasHelp ? 'slq-qtext slq-qhelp' : 'slq-qtext');
    body.innerHTML = '';

    if (q.type === 'single-choice' || q.type === 'multiple-choice') {
      renderChoiceOptions(q, body);
    } else if (q.type === 'textarea') {
      renderTextInput(q, body, true);
    } else {
      renderTextInput(q, body, false);
    }

    applyQuestionCustomizations(q, body);

    var cont = $('[data-slq-continue]');
    if (cont) {
      var needsContinue = q.type !== 'single-choice';
      cont.toggleAttribute('hidden', !needsContinue);
      cont.textContent = q.buttonLabel || 'Continue';
    }
    // ZIP is optional and US-only; Skip lets international takers past it.
    var skip = $('[data-slq-skip]');
    if (skip) skip.toggleAttribute('hidden', q.id !== 'q19');

    var back = $('[data-slq-back]');
    if (back) {
      var prev = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'previous');
      back.toggleAttribute('hidden', prev === -1);
    }

    // Move focus to the new question heading. Without this the DOM swaps
    // underneath a keyboard/screen-reader user while focus stays parked on the
    // Continue button they just pressed — the question silently changes and
    // nothing is announced. tabindex="-1" makes the heading programmatically
    // focusable without adding it to the tab order. Skipped on the very first
    // paint so opening the quiz doesn't yank focus mid-scroll.
    if (qtext && state.hasRenderedAQuestion) {
      qtext.setAttribute('tabindex', '-1');
      try { qtext.focus({ preventScroll: true }); } catch (e) { qtext.focus(); }
    }
    state.hasRenderedAQuestion = true;
  }

  /* FALLBACK "Why we ask this" copy, transcribed verbatim from Dr. Baumann's
     "Quiz text 2026" brief. Keyed by question id, value is an array of
     paragraphs.

     THIS MAP IS NO LONGER THE SOURCE OF TRUTH. renderQuestionInfo() prefers
     `question.whyWeAsk`, served by /api/quiz/questions, which carries whatever
     Dr. Baumann published in STS-Data. The map renders only when nothing is
     published for that question, when the API build predates the overlay, or
     when the content service was unreachable — it is the safety net that keeps
     the explanations on screen during an outage, not the copy she edits.
     Editing it changes live copy only until something is published for that
     question id, at which point the published text wins. Publish instead.

     Scope note: the brief ALSO proposes rewrites of several question stems and
     help texts. Those are deliberately NOT applied here — this change adds the
     explanations only and leaves every question and option exactly as it is.

     Questions with no entry render no toggle at all. The brief marks the two
     concerns questions (q9 / q9-2), both sunscreen questions (q11 / q12) and
     the background question (q16) as "lets discuss", so they have no approved
     copy yet and are intentionally absent. */
  var QUESTION_INFO = {
    // Q1 — moisturization
    'q3': [
      'How often your skin needs moisturizer and how it reacts to soap give us clues about your skin barrier and natural oil production. This helps us choose the right cleansers and moisturizers for you.'
    ],
    // Q2 — sebum production
    'q4': [
      'How oily or dry your skin seems is only one clue to how much sebum it makes. These answers help us estimate your sebum production and which ingredients to include or avoid throughout your routine, from cleansers and serums to moisturizers and sunscreens.'
    ],
    // Q3 — underlying inflammation
    'q5': [
      'Reactive skin has many causes, and you may have more than one cause of inflammation at the same time. These answers help uncover which of the four sensitive skin types you have, and you may have several. When sensitivities overlap, choosing products becomes more complicated because what helps one concern may aggravate another. That’s where a skin-type-specific routine really matters.',
      'If products cause pimples, redness, burning, or stinging, don’t choose “allergic reactions.” Instead, choose the symptom you experience. Choose “allergic reactions” only if you’ve had a recurring reaction to a skincare product or ingredient, such as an itchy, red, scaling rash or hives.',
      'If you tell us that the smell of fragrances bothers you, we will choose products that do not have a strong smell. If you are fragrance allergic, choose the allergic option and the “The smell of fragrance bothers me” option.'
    ],
    // Q4 — pigmentation / skin lighteners
    'q6': [
      'Your answer determines whether our personalized skincare recommendations target dark spots and uneven pigmentation in your routine. Inflammation experienced by the four sensitive skin types can trigger or worsen pigmentation. Sometimes we need to control inflammation or repair the skin barrier before aggressively treating dark spots.',
      'Your Baumann Skin Type helps us decide not only which products to use, but which skin concerns to treat first and how to layer your routine so the products work well together.'
    ],
    // Q5 — lifestyle habits
    'q7': [
      'Age matters, but daily habits also determine whether your skin is wrinkle-prone. A W-type predicts future aging risk, even if you’re young and wrinkle-free today.',
      'Your answers help us personalize antioxidants, retinoids, and other anti-aging ingredients to counteract the aging effects of your lifestyle while addressing your other skin concerns. We consider your entire skin type: Will a treatment irritate sensitive skin, weaken a fragile skin barrier, or work well on oily skin?',
      'This is where many people go wrong when building a routine without a dermatologist’s input: they treat one concern without considering how each product affects their other skin concerns.'
    ],
    // Q6 — suncare habits
    'q7-2': [
      'UV exposure affects more than aging. Sun damage can weaken your skin barrier, trigger inflammation and dark spots, accelerate skin aging, and increase skin cancer risk.',
      'Your answers help us assess your sun exposure and personalize your sunscreen, antioxidants, and other protective and repair treatments.',
      'Answer honestly about your past and current sun habits. It’s never too late to protect your skin and start improving its health. Coming up, we’ll ask about your sunscreen preferences so we can help you find a sunscreen you’ll actually want to wear every day.'
    ],
    // Q7 — eye product
    'q8': [
      'The delicate skin around your eyes has different needs than the rest of your face, and some facial products may irritate the eye area or sting your eyes.',
      'Tell us what bothers you most. We’ll prioritize your eye concerns and help choose the right cream, gel, or serum to target them, rather than expecting one eye product to do everything.'
    ],
    // Q12 — gender
    'q14': [
      'Hormones can affect oiliness, breakouts, pigmentation, and other skin concerns. Your answer also helps us personalize details such as fragrance preferences, so the products we recommend fit both your skin and your preferences.'
    ],
    // Q13 — pregnancy / breastfeeding
    'q15': [
      'Your safety comes first. Pregnancy and breastfeeding affect which skincare ingredients we recommend. We’ll adjust your routine to avoid ingredients such as retinoids and high concentrations of salicylic acid and choose appropriate alternatives for your skin concerns.',
      'Always discuss new skincare products with your doctor during pregnancy or breastfeeding, especially products applied over large areas of the body, such as body creams and lotions.'
    ]
  };

  /* The published paragraphs for one question, or null if none were published.

     ⚠️ An EMPTY ARRAY is a published answer, not the absence of one. It means
     an editor opened the paragraph boxes and left them empty — she deleted the
     explanation on purpose — so it returns [] and the caller renders nothing.
     Only a MISSING or non-array `whyWeAsk` returns null and falls through to
     the QUESTION_INFO map below.

     This returned `null` for [] once, on the reasoning that the caller could
     then use a plain `||`. The cost was that a deletion could never take
     effect: the publish succeeded, the field arrived as [], this collapsed it
     to null, and the theme's own hardcoded paragraph reappeared as though it
     were current copy. The API half of the same contract is the `why` branch of
     lib/quiz/content-overlay.ts in skin-type-pro, which must emit `whyWeAsk: []`
     rather than omitting the field. Changing one half alone restores the bug.

     Still defensive about the rest: a non-string or blank entry inside the
     array is dropped, so a stray "" cannot open a panel that promises an
     explanation and then shows nothing. */
  function servedWhyWeAsk(q) {
    var raw = q && q.whyWeAsk;
    if (!Array.isArray(raw)) return null;
    var paras = [];
    for (var i = 0; i < raw.length; i++) {
      if (typeof raw[i] === 'string' && raw[i].trim()) paras.push(raw[i]);
    }
    return paras;
  }

  function renderQuestionInfo(q) {
    var wrap = $('[data-slq-qinfo]');
    if (!wrap) return;
    var served = servedWhyWeAsk(q);
    // `!== null`, NEVER a plain `||`: served is [] when the explanation was
    // deleted on purpose, and `[] || fallback` yields [] only by the accident
    // that an empty array is truthy. Saying it explicitly keeps a later reader
    // from "simplifying" the deletion back out of existence.
    var paras = served !== null ? served : (q && QUESTION_INFO[q.id]);
    // Which source rendered is readable straight off the DOM, so confirming
    // that a publish reached the live storefront is one attribute check and
    // not a diff of two paragraphs by eye. 'published' with the panel hidden is
    // a deliberate deletion; 'none' is a question nobody ever wrote copy for.
    wrap.setAttribute('data-slq-qinfo-source',
      served !== null ? 'published' : ((paras && paras.length) ? 'theme' : 'none'));
    if (!paras || !paras.length) { wrap.setAttribute('hidden', ''); return; }

    var toggle = $('[data-slq-qinfo-toggle]');
    var panel = $('[data-slq-qinfo-panel]');
    if (!toggle || !panel) return;

    panel.innerHTML = '';
    paras.forEach(function (text, i) {
      var p = document.createElement('p');
      p.textContent = text;
      if (i) p.style.marginTop = '9px';
      panel.appendChild(p);
    });

    // Always collapse on question change — an explanation left open from the
    // previous question reads as if it belongs to this one.
    panel.setAttribute('hidden', '');
    toggle.setAttribute('aria-expanded', 'false');
    wrap.removeAttribute('hidden');

    if (!toggle.__slqBound) {
      toggle.__slqBound = true;
      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.toggleAttribute('hidden', open);
      });
    }
  }

  function optionLetter(i) {
    return String.fromCharCode(65 + (i % 26));
  }

  function renderChoiceOptions(q, body) {
    var isMulti = q.type === 'multiple-choice';
    q.options.forEach(function (opt, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'slq-opt';
      btn.setAttribute('data-slq-opt-value', opt.value);
      // Default the toggle state so every option exposes it from first paint —
      // markSelected() flips it, but an unselected option must still announce
      // itself as a toggle rather than a plain button.
      btn.setAttribute('aria-pressed', 'false');
      btn.style.cssText = 'border:1.5px solid #DBDCDD;background:#fff;border-radius:14px;padding:12px 15px;font-size:15px;font-weight:500;color:#26284a;display:flex;align-items:center;gap:12px;cursor:pointer;font-family:inherit;text-align:left;width:100%';
      var key = document.createElement('span');
      key.style.cssText = 'border-radius:50%;min-width:28px;height:28px;background:#DBDCDD;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#2F3572;flex:none';
      key.textContent = optionLetter(i);
      btn.appendChild(key);
      btn.appendChild(document.createTextNode(opt.label));
      if (isMulti) {
        if (Array.isArray(state.draft) && state.draft.indexOf(opt.value) !== -1) markSelected(btn, true);
        btn.addEventListener('click', function () { toggleMultiOption(q, btn, opt); });
      } else {
        if (state.draft === opt.value) markSelected(btn, true);
        btn.addEventListener('click', function () { pickSingleOption(q, btn, opt); });
      }
      body.appendChild(btn);
    });
  }

  function markSelected(btn, on) {
    // aria-pressed is the ONLY selection signal a screen reader gets: the
    // selected state is otherwise conveyed purely by border/background colour,
    // so without this a non-sighted user cannot tell which options they picked
    // (and on a multi-select question that is the whole answer).
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (on) {
      btn.classList.add('slq-opt--selected');
      btn.style.borderColor = 'var(--slq-type-deep)';
      btn.style.background = 'var(--slq-type-tint)';
    } else {
      btn.classList.remove('slq-opt--selected');
      btn.style.borderColor = '#DBDCDD';
      btn.style.background = '#fff';
    }
  }

  function pickSingleOption(q, btn, opt) {
    $all('.slq-opt').forEach(function (el) { markSelected(el, false); });
    markSelected(btn, true);
    state.draft = opt.value;
    state.answers[q.id] = opt.value;
    saveProgress();
    // prototype behavior: single-choice advances immediately (brief highlight)
    window.setTimeout(function () { advance(); }, 160);
  }

  function toggleMultiOption(q, btn, opt) {
    if (!Array.isArray(state.draft)) state.draft = [];
    var idx = state.draft.indexOf(opt.value);
    if (idx === -1) {
      if (q.maxSelections && state.draft.length >= q.maxSelections) {
        showError('You can select up to ' + q.maxSelections + ' options.');
        shake();
        return;
      }
      state.draft.push(opt.value);
      markSelected(btn, true);
      // "None of the above" exclusivity (quiz.js handleMultipleChoice:560-607):
      // picking None clears every other selection; picking anything else
      // clears None.
      var NONE = 'None of the above';
      var isNone = (opt.label || '').indexOf(NONE) !== -1;
      var body = btn.parentElement;
      if (body) {
        var buttons = body.querySelectorAll('.slq-opt');
        buttons.forEach(function (other) {
          if (other === btn) return;
          var otherIsNone = other.textContent.indexOf(NONE) !== -1;
          if ((isNone && !otherIsNone) || (!isNone && otherIsNone)) {
            var val = other.getAttribute('data-slq-opt-value');
            var vi = state.draft.indexOf(val);
            if (vi !== -1) state.draft.splice(vi, 1);
            markSelected(other, false);
          }
        });
      }
    } else {
      state.draft.splice(idx, 1);
      markSelected(btn, false);
    }
    hideError();
  }

  function renderTextInput(q, body, isTextarea) {
    var input = document.createElement(isTextarea ? 'textarea' : 'input');
    if (!isTextarea) input.type = 'text';
    input.setAttribute('data-slq-text-input', '');
    input.placeholder = q.placeholder || '';
    input.value = typeof state.draft === 'string' ? state.draft : '';
    input.style.cssText = 'width:100%;border:1.5px solid #DBDCDD;border-radius:12px;padding:14px 16px;font-size:15px;font-family:inherit;color:#26284a;background:#fff' + (isTextarea ? ';min-height:110px;resize:vertical' : '');
    input.addEventListener('input', function () { state.draft = input.value; hideError(); });
    if (!isTextarea) {
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); onContinue(); }
      });
    }
    // MM/YYYY affordance for the birthdate question
    if (q.validationPattern === 'MM/YYYY') {
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('maxlength', '7');
      if (!input.getAttribute('placeholder')) input.setAttribute('placeholder', 'MM/YYYY');
      /* Type digits only — the slash is inserted for you.
         A numeric keyboard has no "/" key, so requiring the visitor to type one
         made this question impossible to answer on a phone. */
      input.addEventListener('input', function (ev) {
        var deleting = !!(ev && typeof ev.inputType === 'string' && ev.inputType.indexOf('delete') === 0);
        var next = formatMonthYear(input.value, deleting);
        if (next !== input.value) {
          input.value = next;
          try { input.setSelectionRange(next.length, next.length); } catch (e) { /* not all inputs allow it */ }
        }
        // The generic handler above already ran and stored the UNFORMATTED
        // value, so re-sync the draft to what is actually on screen.
        state.draft = input.value;
      });
    }
    body.appendChild(input);
    window.setTimeout(function () { try { input.focus(); } catch (e) { /* noop */ } }, 50);
  }

  function showError(msg) {
    var el = $('[data-slq-qerror]');
    if (!el) return;
    el.textContent = msg;
    el.removeAttribute('hidden');
  }

  function hideError() {
    var el = $('[data-slq-qerror]');
    if (!el) return;
    el.setAttribute('hidden', '');
  }

  function shake() {
    var card = $('[data-slq-qcard]');
    if (!card) return;
    card.style.animation = 'none';
    void card.offsetWidth; // restart animation
    card.style.animation = 'slq-shake .4s ease';
  }

  // Continue for multi/text/textarea questions
  /* Practice-code check (q1).
   *
   * An unrecognised code used to sail through all 18 questions and only blow up
   * at submit: the scoring Lambda cannot resolve it, so the taker got a bare
   * 500, no results, and retrying failed identically. Catching it here is the
   * difference between "fix this one field" and "your quiz is gone".
   *
   * FAILS OPEN. If the check itself errors or times out, the visitor continues —
   * an outage in the validator must never block the funnel; the worst case is
   * the old behaviour.
   */
  var practiceCodeCache = {};
  var practiceCodeInFlight = false;

  function checkPracticeCode(code, done) {
    var key = code.toUpperCase();
    if (practiceCodeCache[key]) { done(practiceCodeCache[key]); return; }
    if (practiceCodeInFlight) return;
    practiceCodeInFlight = true;

    var cont = $('[data-slq-continue]');
    var restore = cont ? cont.textContent : null;
    if (cont) { cont.disabled = true; cont.textContent = 'Checking…'; }
    var settle = function (verdict) {
      practiceCodeInFlight = false;
      if (cont) { cont.disabled = false; if (restore !== null) cont.textContent = restore; }
      if (verdict !== 'unknown') practiceCodeCache[key] = verdict;
      done(verdict);
    };

    var url = stripTrailingSlash(SLQ.settings.nextBase) +
      '/api/quiz/validate-affiliate?code=' + encodeURIComponent(code);
    var timer = window.setTimeout(function () { settle('unknown'); }, 6000);

    window.fetch(url, { method: 'GET' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        window.clearTimeout(timer);
        if (!data || typeof data.isValid !== 'boolean') { settle('unknown'); return; }
        settle(data.isValid ? 'valid' : 'invalid');
      })
      .catch(function () { window.clearTimeout(timer); settle('unknown'); });
  }

  // Skip on the ZIP step: same as a blank Continue (stored '' is dropped at submit).
  function onSkip() {
    var q = currentQuestion();
    if (!q || q.id !== 'q19') return;
    state.draft = '';
    state.answers[q.id] = '';
    saveProgress();
    advance();
  }

  function onContinue() {
    var q = currentQuestion();
    if (!q) return;
    var value = state.draft;
    if (q.type === 'multiple-choice' && !Array.isArray(value)) value = [];

    // validation ports
    if (q.id === 'q1') {
      var codeRaw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (state.q1Locked && state.affiliateCode) {
        // Confirmation screen for a known practice code: it arrived in ?code=
        // (or storage) and there is no box the visitor could correct. Still
        // check it, because affiliateNames.json carries codes that no longer
        // exist in the backend (40 of 246 on 2026-10-02 match no row in
        // affiliate, companies or company_locations). Sending one of those on
        // makes the scoring Lambda throw and the taker gets no results at all,
        // so a definitive rejection drops quietly to the house default --
        // which is what happens today -- instead of showing an error on a
        // hidden field. Anything inconclusive keeps the code: that is the
        // whole point of the fix.
        var lockedCode = state.affiliateCode;
        checkPracticeCode(lockedCode, function (verdict) {
          if (verdict === 'invalid') {
            state.affiliateCode = '';
            state.answers[q.id] = '';
          } else {
            state.answers[q.id] = lockedCode;
          }
          saveProgress();
          advance();
        });
        return;
      }
      if (!codeRaw) {
        // Cleared on purpose. Drop the ARRIVING code too: submit resolves
        // `typed || stored || STSMD`, so leaving state.affiliateCode set would
        // quietly re-use the very code the visitor just erased — which is the
        // unrecognised one that breaks scoring.
        state.affiliateCode = '';
        state.answers[q.id] = '';
        saveProgress();
        advance();
        return;
      }
      checkPracticeCode(codeRaw, function (verdict) {
        if (verdict === 'invalid') {
          showError('We don’t recognise that practice code. Check it with your provider, or clear the box to continue without one.');
          shake();
          return;
        }
        state.answers[q.id] = codeRaw;
        // The code was accepted, so it is now THE code. Without this every
        // mirror keeps whatever arrived in ?code= — submit still sends the
        // typed one, but the storefront chrome, the 'Practice code applied'
        // note and any downstream localStorage reader stay on the old code.
        // Correcting one valid practice to another would otherwise leave the
        // visitor attributed two different ways at once.
        state.affiliateCode = codeRaw;
        lsSet('affiliateCode', codeRaw);
        ssSet('affiliateCode', codeRaw);   // ssGet wins in resolveAffiliateCode
        try { document.body.setAttribute('data-affiliate-code', codeRaw); } catch (e) { /* noop */ }
        saveProgress();
        advance();
      });
      return;
    }
    if (q.id === 'q17') {
      var nameErr = validateName(Array.isArray(value) ? (value[0] || '') : (value || ''));
      if (nameErr) { showError(nameErr); shake(); return; }
    } else if (!validateAnswer(q, value)) {
      showError('This question is required.');
      shake();
      return;
    }
    if (q.validationPattern === 'MM/YYYY') {
      var raw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (raw && !isValidMonthYear(raw)) {
        showError('Please enter your birth date as MM/YYYY (for example 04/1985).');
        shake();
        return;
      }
      value = raw;
    }
    // ZIP: optional, but when provided it must be a real US ZIP (5 or 5+4).
    if (q.id === 'q19') {
      var zipRaw = (Array.isArray(value) ? (value[0] || '') : (value || '')).trim();
      if (zipRaw && !/^\d{5}(-\d{4})?$/.test(zipRaw)) {
        showError('Please enter a valid ZIP code (for example 33131).');
        shake();
        return;
      }
      value = zipRaw;
    }
    state.answers[q.id] = value;
    saveProgress();
    advance();
  }

  function advance() {
    var answered = currentQuestion();
    if (answered) SLQ.track('question_answered', { question_code: answered.id, question_type: answered.type });
    if (answered && (answered.type === 'single-choice' || answered.type === 'multiple-choice')) {
      var v = state.answers[answered.id];
      var f = gaQuestionFields(answered);
      f.question_value = Array.isArray(v) ? v.join(', ') : (typeof v === 'string' ? v : undefined);
      gaEvent('questionAnswered', f);
    }
    var next = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'next');
    if (next === -1) {
      saveProgress();
      openGate();
      return;
    }
    state.currentIndex = next;
    saveProgress(); // checkpoint AFTER moving so a reload resumes on the next question
    renderQuestion();
    window.scrollTo({ top: 0 });
  }

  function goBack() {
    var prev = getNextVisibleQuestion(state.questions, state.currentIndex, state.answers, 'previous');
    if (prev === -1) return;
    state.currentIndex = prev;
    saveProgress();
    renderQuestion();
    window.scrollTo({ top: 0 });
  }

  // ---------------------------------------------------------------- email gate

  var turnstileWidgetId = null;
  var turnstileLoading = false;
  var turnstilePending = null; // resolver for an in-flight token refresh

  function openGate() {
    var gate = $('[data-slq-gate]');
    if (!gate) return;
    gate.removeAttribute('hidden');
    ensureTurnstile();
    var email = $('[data-slq-gate-email]');
    if (email) {
      if (state.email) email.value = state.email;
      else {
        // Prefill: the logged-in customer (rendered by Liquid, authoritative),
        // else the returning-taker hint (quiz.js setQuizInputs:380) — read-only.
        var storedEmail = gateLoggedInEmail() || lsGet('customerEmail');
        if (storedEmail && !email.value) email.value = storedEmail;
      }
      scheduleGateEmailCheck(true);
      window.setTimeout(function () { try { email.focus(); } catch (e) { /* noop */ } }, 80);
    }
  }

  function closeGate() {
    var gate = $('[data-slq-gate]');
    if (gate) gate.setAttribute('hidden', '');
  }

  function gateError(msg) {
    var el = $('[data-slq-gate-error]');
    if (!el) return;
    if (msg) { el.textContent = msg; el.removeAttribute('hidden'); }
    else { el.setAttribute('hidden', ''); }
  }

  // ---------------------------------------- gate: who is typing? (pre-check)
  /* Before the shopper submits, ask the API whether the typed email already
     belongs to a store account ('account'), to a not-yet-activated record or
     a result we already hold ('activate'), or to nobody ('new'), and shape the
     gate to match: the optional password box only makes sense for a brand-new
     shopper (an existing account keeps its password; an existing record is
     activated from the emailed link), and "claim $5" is only true for a
     first-time taker. Coarse, best-effort: any failure reads as 'new', and the
     post-submit routing (staged panel / activation refusal) stays the safety
     net. Never blocks the submit — a fast typer just gets today's behaviour. */
  var GATE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var GATE_KNOWN_STATES = ['account', 'activate', 'results', 'known'];
  var GATE_LABEL_NEW = 'Email my results + claim $10';
  var GATE_LABEL_KNOWN = 'Email my results';
  var GATE_INTRO_KNOWN = 'We\u2019ll email your skin type and routine so you never lose them.';
  var gateIntroNew = null; // the Liquid copy ($5 + password), captured on first use
  var gateCheckCache = {};
  var gateCheckSeq = 0;
  var gateCheckTimer = null;
  var gateEmailState = 'new';

  function gateLoggedInEmail() {
    return String(root.getAttribute('data-customer-email') || '').trim().toLowerCase();
  }

  function gateLabel() { return gateEmailState === 'new' ? GATE_LABEL_NEW : GATE_LABEL_KNOWN; }

  function checkGateEmail(email) {
    var key = String(email || '').trim().toLowerCase();
    if (!key || !GATE_EMAIL_RE.test(key) || /@temp\.quiz\.internal$/i.test(key)) return Promise.resolve('new');
    if (gateCheckCache[key]) return Promise.resolve(gateCheckCache[key]);
    var base = String(SLQ.settings.ec2Base || '').replace(/\/+$/, '');
    if (!base) return Promise.resolve('new');
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? window.setTimeout(function () { try { ctrl.abort(); } catch (e) { /* noop */ } }, 6000) : null;
    return fetch(base + '/widget/gate/email-check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: key }),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (timer) window.clearTimeout(timer);
        var st = (j && GATE_KNOWN_STATES.indexOf(j.state) !== -1) ? j.state : 'new';
        if (j && !j.limited) gateCheckCache[key] = st;
        return st;
      })
      .catch(function () { if (timer) window.clearTimeout(timer); return 'new'; });
  }

  function applyGateEmailState(st, email) {
    var known = GATE_KNOWN_STATES.indexOf(st) !== -1;
    if (st !== gateEmailState && known) SLQ.track('gate_email_known', { state: st });
    gateEmailState = st;
    var pw = $('[data-slq-gate-password]');
    var hint = $('[data-slq-gate-hint]');
    var intro = $('[data-slq-gate-intro]');
    var submit = $('[data-slq-gate-submit]');
    var typed = String(email || '').trim().toLowerCase();
    var me = gateLoggedInEmail();
    if (pw) {
      if (known) { pw.value = ''; pw.setAttribute('hidden', ''); }
      else pw.removeAttribute('hidden');
    }
    // The intro promises $5 and offers a password — both only true for a
    // brand-new shopper. Known email: keep just the "we'll email it" line.
    if (intro) {
      if (gateIntroNew === null) gateIntroNew = intro.innerHTML;
      var wantKnown = known;
      if (wantKnown && intro.getAttribute('data-slq-gate-intro') !== 'known') {
        intro.textContent = GATE_INTRO_KNOWN; intro.setAttribute('data-slq-gate-intro', 'known');
      } else if (!wantKnown && intro.getAttribute('data-slq-gate-intro') === 'known') {
        intro.innerHTML = gateIntroNew; intro.setAttribute('data-slq-gate-intro', '');
      }
    }
    if (hint) {
      hint.textContent = '';
      if (st === 'account' && me && me === typed) {
        hint.textContent = 'You\u2019re logged in \u2014 today\u2019s results will be saved to your account.';
      } else if (st === 'account' && me) {
        // Logged in as someone else: /account/login would just bounce to
        // /account, so no link \u2014 the emailed link (staged flow) is the door.
        hint.textContent = 'This email has its own account, but you\u2019re logged in as ' + me + '. Continue and we\u2019ll email that address a link to apply today\u2019s results \u2014 or log out and log in with it first.';
      } else if (st === 'account') {
        hint.appendChild(document.createTextNode('Welcome back \u2014 this email already has an account with us. '));
        var a = document.createElement('a');
        a.setAttribute('data-slq-gate-login', '');
        a.href = '/account/login?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
        a.textContent = 'Log in first';
        a.style.cssText = 'color:#2F3572;font-weight:600;text-decoration:underline';
        hint.appendChild(a);
        hint.appendChild(document.createTextNode(' and today\u2019s results save straight to your account \u2014 or continue, and we\u2019ll email you a link to apply them.'));
      } else if (st === 'activate') {
        // disabled store record + results on file -> the submit is staged; the
        // panel offers "Activate my account" and a link goes out by email.
        hint.textContent = 'This email already has results on file, but its account hasn\u2019t been activated yet. We\u2019ll email you a link to save today\u2019s results to them.';
      } else if (st === 'results') {
        // results on file, no store record (portal / partner / legacy taker)
        // -> staged; the emailed link is the only door.
        hint.textContent = 'We already have results for this email. We\u2019ll email you a link to save today\u2019s results to them.';
      } else if (st === 'known') {
        // disabled store record, no results (a guest-checkout customer who
        // never took the quiz) -> the submit succeeds as a guest; a password
        // typed here would be refused (the record is not this run's).
        hint.textContent = 'This email is already registered with the store. We\u2019ll email your results \u2014 activate your account any time to log in and keep them.';
      }
      hint.toggleAttribute('hidden', !known);
    }
    if (submit && !state.submitting) submit.textContent = gateLabel();
  }

  function scheduleGateEmailCheck(immediate) {
    var input = $('[data-slq-gate-email]');
    if (!input) return;
    var email = String(input.value || '').trim().toLowerCase();
    if (gateCheckTimer) { window.clearTimeout(gateCheckTimer); gateCheckTimer = null; }
    if (!email || !GATE_EMAIL_RE.test(email)) { applyGateEmailState('new', email); return; }
    var run = function () {
      gateCheckTimer = null;
      var seq = ++gateCheckSeq;
      checkGateEmail(email).then(function (st) {
        if (seq !== gateCheckSeq) return; // a newer check superseded this one
        var cur = $('[data-slq-gate-email]');
        if (!cur || String(cur.value || '').trim().toLowerCase() !== email) return; // typed on since
        applyGateEmailState(st, email);
      });
    };
    if (immediate || gateCheckCache[email]) run();
    else gateCheckTimer = window.setTimeout(run, 500);
  }

  /* "Log in first": Shopify's return_url brings the shopper back to this very
     page (answers are checkpointed per tab), where the gate re-opens with the
     account email and the results then save straight to the account. Clear
     the legacy checkout_url hint first — snippets/quiz.liquid would otherwise
     bounce a freshly logged-in customer with saved results to the RESULTS
     page instead of back here. */
  function onGateLoginFirst() {
    saveProgress();
    try { localStorage.removeItem('checkout_url'); } catch (e) { /* noop */ }
    SLQ.track('gate_login_first');
  }

  // Turnstile pre-wire (spec §5/D11): blank site key -> render nothing, omit
  // turnstileToken (identical to today). Key set -> render explicit widget.
  function ensureTurnstile() {
    var key = SLQ.settings.turnstileSiteKey;
    if (!key || turnstileWidgetId !== null || turnstileLoading) return;
    turnstileLoading = true;
    var renderWidget = function () {
      var host = $('[data-slq-turnstile]');
      if (!host || !window.turnstile) return;
      try {
        turnstileWidgetId = window.turnstile.render(host, {
          sitekey: key,
          callback: function (token) { if (turnstilePending) turnstilePending(token); },
          'error-callback': function () { if (turnstilePending) turnstilePending(null); },
          'expired-callback': function () { if (turnstilePending) turnstilePending(null); }
        });
      } catch (e) { /* Turnstile render failed: submit proceeds token-less; server decides */ }
    };
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      renderWidget();
      return;
    }
    window.__slqTurnstileReady = renderWidget;
    var s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__slqTurnstileReady&render=explicit';
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
  }

  function readTurnstileToken() {
    if (turnstileWidgetId === null || !window.turnstile) return null;
    try {
      var t = window.turnstile.getResponse(turnstileWidgetId);
      return t || null;
    } catch (e) { return null; }
  }

  /* Cloudflare Turnstile tokens are SINGLE-USE and short-lived. The submit
     flow can retry (network/5xx x2, plus the 409 in-flight poll up to ~2 min),
     so reusing the gate's token would fail verification with
     "timeout-or-duplicate" the moment Turnstile keys are provisioned. Each
     retry therefore resets the widget and waits for a brand-new token.
     Resolves null (never throws, never blocks the funnel) when Turnstile is
     absent, errors, or does not answer within the timeout — the server then
     applies its own policy. */
  var TURNSTILE_REFRESH_TIMEOUT_MS = 8000;

  /* Wait for the Turnstile script + widget to actually exist. A visitor can
     submit the gate within a few hundred ms of it opening — long before
     Cloudflare's script has loaded — and without this the request would carry
     no token at all (invisible today because verification fails open, but a
     403 for real people the moment keys are provisioned). */
  function whenTurnstileReady(timeoutMs) {
    return new Promise(function (resolve) {
      if (!SLQ.settings || !SLQ.settings.turnstileSiteKey) { resolve(false); return; }
      if (turnstileWidgetId !== null) { resolve(true); return; }
      ensureTurnstile();
      var waited = 0;
      var tick = window.setInterval(function () {
        if (turnstileWidgetId !== null) { window.clearInterval(tick); resolve(true); return; }
        waited += 100;
        if (waited >= timeoutMs) { window.clearInterval(tick); resolve(false); }
      }, 100);
    });
  }

  function refreshTurnstileToken() {
    return new Promise(function (resolve) {
      if (turnstileWidgetId === null || !window.turnstile) { resolve(null); return; }
      var settled = false;
      var finish = function (token) {
        if (settled) return;
        settled = true;
        turnstilePending = null;
        resolve(token || null);
      };
      turnstilePending = finish;
      window.setTimeout(function () { finish(readTurnstileToken()); }, TURNSTILE_REFRESH_TIMEOUT_MS);
      try {
        window.turnstile.reset(turnstileWidgetId);
        // Explicit-execution widgets need an execute() kick; for auto widgets
        // reset() alone re-runs the challenge and fires the callback.
        if (typeof window.turnstile.execute === 'function') {
          try { window.turnstile.execute(turnstileWidgetId); } catch (e) { /* auto mode */ }
        }
      } catch (e) { finish(null); }
    });
  }

  /* The outcome panel's "Try again".
   *
   * state.turnstileToken holds the token the gate spent on the attempt that
   * just failed, and Turnstile tokens are SINGLE-USE: re-sending one verifies
   * as "timeout-or-duplicate", so the server answers 403 "Bot verification
   * failed" regardless of what caused the ORIGINAL failure. Observed live
   * 2026-08-10 — a 500 retried straight into a 403, so the button could never
   * recover anything. submitWithRetries already re-mints between its own
   * attempts; this outcome-panel retry was the one path that skipped it.
   *
   * Failing to obtain a fresh token does NOT block the retry: we submit with a
   * null token and let the server apply its own policy. That is the same
   * posture the gate takes when the Turnstile script is blocked or absent, so
   * a visitor behind a strict blocker is never trapped by their own retry. */
  function retryWithFreshToken() {
    if (state.submitting) return;
    var configured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
    if (!configured) { startSubmit(); return; }

    var btn = $('[data-slq-submit-retry]');
    var restoreLabel = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Verifying…'; }

    // NB: not named `go` — that is the module-level view switcher.
    var resubmit = function (tok) {
      state.turnstileToken = tok || null;
      if (btn) { btn.disabled = false; btn.textContent = restoreLabel; }
      startSubmit();
    };

    whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
      if (!ready) { resubmit(null); return; }
      refreshTurnstileToken().then(resubmit);
    });
  }

  /* Both gate exits — "Email my results" and "Skip" — run through here.
     `skip` leaves state.email empty; the orchestrator then generates a
     guest_*@temp.quiz.internal address (app/api/quiz/submit/route.ts), so
     results are never paywalled behind an email.

     The captcha is enforced on BOTH paths and is deliberately stricter on
     skip: with an email we can rate-limit and de-duplicate by submission
     fingerprint, but a skipped submission has no such handle, so Turnstile is
     the only thing between a bot and the scoring Lambda. When Turnstile is
     configured but has not produced a token yet we wait for it rather than
     letting the request through — the whole point of the skip path is that a
     human passes it silently and a bot cannot. */
  function proceedFromGate(skip) {
    if (state.submitting) return;
    var emailInput = $('[data-slq-gate-email]');
    var consentInput = $('[data-slq-gate-consent]');
    var passwordInput = $('[data-slq-gate-password]');
    var hpInput = $('[data-slq-hp]');
    var email = emailInput ? String(emailInput.value || '').trim() : '';
    // Optional: a password turns the store record this quiz creates into a
    // real account (see finishSuccess -> activateGateAccount). Never required.
    var password = (!skip && passwordInput && !passwordInput.hasAttribute('hidden')) ? String(passwordInput.value || '') : '';

    if (!skip) {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        gateError('Enter a valid email, or choose “Skip” to see your results without one.');
        return;
      }
      if (password && (password.length < 8 || password.length > 100 || /^\s|\s$/.test(password))) {
        gateError('Passwords need at least 8 characters (no spaces at the start or end) — or leave it blank to skip the account.');
        return;
      }
    }

    var turnstileConfigured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
    var proceed = function (token) {
      if (turnstileConfigured && !token) {
        gateError('We couldn’t verify you’re human. Please wait a moment and try again.');
        setGateBusy(false);
        return;
      }
      gateError(null);
      state.email = skip ? '' : email;
      state.password = skip ? '' : password;
      state.marketingConsent = skip ? false : !!(consentInput && consentInput.checked);
      state.honeypot = hpInput ? String(hpInput.value || '') : '';
      state.turnstileToken = token;
      setGateBusy(false);
      closeGate();
      startSubmit();
    };

    if (!turnstileConfigured) { proceed(null); return; }
    var existing = readTurnstileToken();
    if (existing) { proceed(existing); return; }
    // Widget still solving (or not yet mounted): wait, don't wave it through.
    setGateBusy(true);
    whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
      if (!ready) { proceed(null); return; } // script blocked/absent — server decides
      var tok = readTurnstileToken();
      if (tok) { proceed(tok); return; }
      refreshTurnstileToken().then(proceed);
    });
  }

  function setGateBusy(busy) {
    var submit = $('[data-slq-gate-submit]');
    var skip = $('[data-slq-gate-skip]');
    [submit, skip].forEach(function (b) {
      if (!b) return;
      b.disabled = !!busy;
      b.style.opacity = busy ? '.6' : '';
    });
    if (submit) submit.textContent = busy ? 'Verifying…' : gateLabel();
  }

  function onGateSubmit(ev) {
    ev.preventDefault();
    proceedFromGate(false);
  }

  // ------------------------------------------------------------- calculating

  function resetCalcUI() {
    var anim = $('[data-slq-calc-anim]');
    if (anim) anim.removeAttribute('hidden');
    ['staged', 'login', 'failed'].forEach(function (k) {
      var el = $('[data-slq-outcome-' + k + ']');
      if (el) el.setAttribute('hidden', '');
    });
    $all('[data-slq-calc-row]').forEach(function (row) { setCalcRow(row, 'pending'); });
    var bar = $('[data-slq-calc-bar]');
    if (bar) bar.style.width = '0%';
    state.calcStage = 0;
  }

  function setCalcRow(row, rowState) {
    row.setAttribute('data-state', rowState);
    var done = row.querySelector('[data-slq-calc-done]');
    var active = row.querySelector('[data-slq-calc-active]');
    var pending = row.querySelector('[data-slq-calc-pending]');
    if (done) done.toggleAttribute('hidden', rowState !== 'done');
    if (active) active.toggleAttribute('hidden', rowState !== 'active');
    if (pending) pending.toggleAttribute('hidden', rowState !== 'pending');
  }

  // Prototype startCalc() cadence (950ms per stage); the LAST row stays active
  // (spinner) until the real submit round-trip resolves — no fabricated finish.
  function startCalcAnimation() {
    clearCalcTimers();
    resetCalcUI();
    var rows = $all('[data-slq-calc-row]');
    var bar = $('[data-slq-calc-bar]');
    if (rows[0]) setCalcRow(rows[0], 'active');
    if (bar) bar.style.width = '12%';
    var advanceStage = function (stage) {
      // stage i: rows[0..i-1] done, rows[i] active; never auto-complete the last row
      for (var i = 0; i < rows.length; i++) {
        if (i < stage) setCalcRow(rows[i], 'done');
        else if (i === stage) setCalcRow(rows[i], 'active');
        else setCalcRow(rows[i], 'pending');
      }
      if (bar) bar.style.width = Math.round(((stage) / rows.length) * 90 + 10) + '%';
    };
    for (var stage = 1; stage < rows.length; stage++) {
      (function (s) {
        state.calcTimers.push(window.setTimeout(function () { advanceStage(s); }, CALC_STAGE_MS * s));
      })(stage);
    }
  }

  function finishCalcAnimation() {
    clearCalcTimers();
    $all('[data-slq-calc-row]').forEach(function (row) { setCalcRow(row, 'done'); });
    var bar = $('[data-slq-calc-bar]');
    if (bar) bar.style.width = '100%';
  }

  function clearCalcTimers() {
    state.calcTimers.forEach(function (t) { window.clearTimeout(t); });
    state.calcTimers = [];
  }

  // ------------------------------------------------------------------- submit

  function submitUrl() {
    if (SLQ.settings.useProxySubmit) {
      return stripTrailingSlash(SLQ.settings.proxyBase) + '/submit';
    }
    return stripTrailingSlash(SLQ.settings.nextBase) + '/api/quiz/submit';
  }

  /* Per-attempt nonce (spec D22 / §13.2 step 1).
   *
   * Shopify's App Proxy signature covers a per-SECOND timestamp and the query
   * params — never the POST body. Two attempts inside the same second are
   * therefore byte-identical, and the façade's single-use replay cache cannot
   * tell a legitimate retry from an attacker's replay: it 401s the retry, and
   * 401 is in DETERMINISTIC_NO_RETRY_STATUSES, so the shopper's quiz dies for
   * good. Appending one fresh UUID per attempt makes every attempt's signature
   * unique, which is what lets the replay cache be strict without eating real
   * retries.
   *
   * This is the ONLY parameter the client may ever append (spec SEC-11): a
   * random UUID is neither PII nor a credential. submissionId, session_uuid,
   * answers and email all stay byte-identical across retries so the ledger's
   * idempotency fingerprint is unaffected. */
  function withNonce(url) {
    var nonce;
    try {
      nonce = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
          });
    } catch (e) { return url; }
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'nonce=' + nonce;
  }

  function attemptSubmit(url, bodyString) {
    return window.fetch(withNonce(url), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: bodyString
    }).then(function (response) {
      return response.json().catch(function () { return null; }).then(function (data) {
        if (response.status === 409) {
          if (data && data.code === 'SUBMISSION_IN_FLIGHT') {
            var retryAfter = (data && typeof data.retryAfter === 'number') ? data.retryAfter : 5;
            return { kind: 'in-flight', retryAfter: retryAfter };
          }
          if (data && data.code === 'LOGIN_REQUIRED') {
            return { kind: 'login-required', email: data.email, message: data.message };
          }
          return { kind: 'server-error', status: response.status };
        }
        if (!response.ok) return { kind: 'server-error', status: response.status };
        if (data && data.requireLoginToSave) return { kind: 'staged', body: data };
        if (data && data.replayed && data.recovered) return { kind: 'recovered', body: data };
        return { kind: 'success', body: data };
      });
    }).catch(function () {
      return { kind: 'network-error' };
    });
  }

  // One attempt-set = one submissionId, and the ledger's idempotency
  // fingerprint is sha256(email | answers) — so answers, email and
  // submissionId stay byte-identical across every internal retry (spec §5).
  // The ONLY field re-issued per attempt is turnstileToken, which must be
  // fresh because Turnstile tokens are single-use; it is outside the
  // fingerprint, so idempotency is unaffected.
  // Budgets: 409-in-flight poll x24 @5s; network/5xx x2 @1s; 400/401/403 never.
  function submitJourney(bodyObject) {
    var url = submitUrl();
    var networkRetries = 0;
    var inFlightAttempts = 0;
    var attempted = false;

    function bodyForAttempt() {
      var configured = !!(SLQ.settings && SLQ.settings.turnstileSiteKey);
      if (!configured) { attempted = true; return Promise.resolve(JSON.stringify(bodyObject)); }

      var isFirst = !attempted;
      attempted = true;
      // First attempt: keep the gate's token if it already exists, otherwise
      // wait for the widget to finish loading and issue one. Retries: always a
      // brand-new token, since Turnstile tokens are single-use.
      if (isFirst && bodyObject.turnstileToken) return Promise.resolve(JSON.stringify(bodyObject));

      return whenTurnstileReady(TURNSTILE_REFRESH_TIMEOUT_MS).then(function (ready) {
        if (!ready) { delete bodyObject.turnstileToken; return JSON.stringify(bodyObject); }
        var existing = isFirst ? readTurnstileToken() : null;
        if (existing) { bodyObject.turnstileToken = existing; return JSON.stringify(bodyObject); }
        return refreshTurnstileToken().then(function (token) {
          if (token) bodyObject.turnstileToken = token;
          else delete bodyObject.turnstileToken;
          return JSON.stringify(bodyObject);
        });
      });
    }

    function loop() {
      return bodyForAttempt().then(function (bodyString) {
        return attemptSubmit(url, bodyString);
      }).then(function (result) {
        if (result.kind === 'network-error' || result.kind === 'server-error') {
          var deterministic = result.kind === 'server-error' &&
            DETERMINISTIC_NO_RETRY_STATUSES.indexOf(result.status) !== -1;
          if (deterministic || networkRetries >= NETWORK_MAX_RETRIES) {
            return {
              kind: 'failed',
              code: result.kind === 'network-error' ? 'NETWORK_ERROR' : ('SERVER_ERROR_' + result.status)
            };
          }
          networkRetries++;
          return delay(NETWORK_RETRY_DELAY_MS).then(loop);
        }
        if (result.kind === 'in-flight') {
          inFlightAttempts++;
          if (inFlightAttempts >= IN_FLIGHT_MAX_ATTEMPTS) {
            return { kind: 'failed', code: 'SUBMISSION_IN_FLIGHT_EXHAUSTED' };
          }
          return delay(result.retryAfter * 1000).then(loop);
        }
        return result; // login-required | staged | recovered | success
      });
    }
    return loop();
  }

  function startSubmit() {
    if (state.submitting) return;

    // Last line of defence: never POST a partial answer set. Scoring a partial
    // set returns 500 and the outcome panel's "Try again" just re-sends it, so
    // the shopper is stuck. Send them to the gap instead — their answers and
    // their place in the quiz are preserved.
    var gap = firstIncompleteQuestionIndex();
    if (gap !== -1) {
      SLQ.track('quiz_submit_blocked_incomplete', { questionId: state.questions[gap].id });
      state.currentIndex = gap;
      go('quiz');
      renderQuestion();
      showError('Please answer this question so we can score your quiz.');
      shake();
      return;
    }

    state.submitting = true;

    var submissionId = uuid();
    var body = buildSubmitRequestBody({
      answers: state.answers,
      questions: state.questions,
      email: state.email,
      marketingConsent: state.marketingConsent,
      affiliateCode: state.affiliateCode,
      turnstileToken: state.turnstileToken,
      honeypot: state.honeypot,
      submissionId: submissionId,
      sessionUuid: sessionUuid(),
      configVersion: state.configVersionInt
    });

    go('calculating');
    startCalcAnimation();
    state.calcStartedAt = Date.now();

    submitJourney(body).then(function (outcome) {
      // calculating min display (2.5s) so it never flashes
      var elapsed = Date.now() - state.calcStartedAt;
      var wait = Math.max(0, CALC_MIN_DISPLAY_MS - elapsed);
      return delay(wait).then(function () { return outcome; });
    }).then(function (outcome) {
      state.submitting = false;
      applyOutcome(outcome);
    });
  }

  function applyOutcome(outcome) {
    if (outcome.kind === 'success' || outcome.kind === 'recovered') {
      clearProgress();
      finishCalcAnimation();
      window.setTimeout(function () { finishSuccess(outcome.body || {}); }, 400);
      return;
    }
    clearCalcTimers();
    var anim = $('[data-slq-calc-anim]');
    if (anim) anim.setAttribute('hidden', '');
    if (outcome.kind === 'staged') {
      /* The legacy quiz's contract for a returning taker who is logged OUT:
         a Shopify account exists -> log in (or activate) on THIS store and the
         new result is applied to it. The server told us which case this is
         (stagedShopifyAccount, from a Shopify lookup by email). Enabled ->
         /account/login; disabled|invited -> the activation page; no store
         account -> the emailed link is the only door. Either way the results
         page ASKS before anything is overwritten. The confirmation email is
         sent in every case, so "later" always works too. */
      var body = outcome.body || {};
      var acct = body.stagedShopifyAccount;
      var acctState = acct && acct.exists ? String(acct.state || '').toLowerCase() : '';
      var resultsPath = String(SLQ.settings.resultsUrl || '/pages/skin-type-results').split('?')[0];
      var backTo = resultsPath + '?staged=' + encodeURIComponent(body.stagingNonce || '');
      var stagedTitle = $('[data-slq-outcome-staged-title]');
      var stagedMsg = $('[data-slq-outcome-staged-msg]');
      var stagedLogin = $('[data-slq-outcome-staged-login]');
      var email = body.stagedEmail ? String(body.stagedEmail) : 'this email';
      var meNow = gateLoggedInEmail();
      var stagedLower = body.stagedEmail ? String(body.stagedEmail).trim().toLowerCase() : '';
      if (acctState === 'enabled' && meNow && stagedLower && meNow !== stagedLower) {
        /* Logged in as a DIFFERENT account than the email on the quiz. The
           usual "Log in" would just bounce (/account/login -> /account) — offer
           the log-out instead; checkout_url (set below) brings the next login,
           as the right account, straight back to the claim prompt. */
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 but you\u2019re logged in as another account';
        if (stagedMsg) stagedMsg.textContent = 'These results belong to ' + email + ', and you\u2019re logged in as ' + meNow + '. Log out, then log in with ' + email + ' and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Log out to switch accounts';
          stagedLogin.setAttribute('href', '/account/logout');
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
      } else if (acctState === 'enabled') {
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 log in to apply them';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results saved on your account. Log in and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Log in';
          stagedLogin.setAttribute('href', '/account/login?return_url=' + encodeURIComponent(backTo));
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
      } else if (acctState === 'disabled' || acctState === 'invited') {
        if (stagedTitle) stagedTitle.textContent = 'Your new results are ready \u2014 activate your account to apply them';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results on file, but the account has not been activated yet. Activate it and we\u2019ll ask you to confirm before anything changes. We\u2019ve also emailed ' + email + ' a link, in case you\u2019d rather do it later.';
        if (stagedLogin) {
          stagedLogin.textContent = 'Activate my account';
          stagedLogin.setAttribute('href', '/pages/customer-activate');
          stagedLogin.setAttribute('data-slq-claim-login', backTo);
          stagedLogin.removeAttribute('hidden');
        }
      } else {
        if (stagedTitle) stagedTitle.textContent = 'Check your email to apply your new results';
        if (stagedMsg) stagedMsg.textContent = 'This email already has results on file, so we\u2019ve sent a confirmation link to ' + email + '. Open that email and tap \u201CApply my new results\u201D to save today\u2019s quiz.';
        if (stagedLogin) stagedLogin.setAttribute('hidden', '');
      }
      // A password typed at the gate is only ever applied to the record THIS
      // submission created. This email already had results, so say so plainly
      // instead of leaving the shopper to wonder what happened to it.
      if (state.password && stagedMsg) {
        stagedMsg.textContent += ' The password you just entered was not used \u2014 this email already has an account with the store.';
        state.password = '';
      }
      // Remember where to come back to after login/activation. quiz.liquid
      // (legacy, still in the layout) redirects a freshly logged-in customer
      // to localStorage.checkout_url — the same hand-off the legacy quiz used.
      if (acctState) {
        try { localStorage.setItem('checkout_url', backTo); } catch (e) { /* storage blocked */ }
      }
      var staged = $('[data-slq-outcome-staged]');
      if (staged) staged.removeAttribute('hidden');
      return;
    }
    if (outcome.kind === 'login-required') {
      var loginMsg = $('[data-slq-outcome-login-msg]');
      if (loginMsg) {
        loginMsg.textContent = outcome.message ||
          ('This email' + (outcome.email ? (' (' + outcome.email + ')') : '') + ' belongs to an existing account. Please log in to save your results.');
      }
      var login = $('[data-slq-outcome-login]');
      if (login) login.removeAttribute('hidden');
      return;
    }
    // failed
    var failedMsg = $('[data-slq-outcome-failed-msg]');
    if (failedMsg) {
      failedMsg.textContent = 'We couldn’t score your quiz (' + (outcome.code || 'UNKNOWN') + '). Your answers are still here — please try again.';
    }
    var failed = $('[data-slq-outcome-failed]');
    if (failed) failed.removeAttribute('hidden');
  }

  // ------------------------------------------------------------------ success

  /* POST /widget/account/activate with the freshly minted widget token. Resolves
     to the Multipass login URL on success, or null on ANY refusal/failure
     (409 = the email already has an account or an older unactivated record —
     the emailed link / "Log in" remain the door; 503 = box not configured).
     The password is sent once over TLS and dropped from state immediately. */
  function activateGateAccount(password, resultsUrl) {
    var base = String(SLQ.settings.ec2Base || '').replace(/\/+$/, '');
    if (!base || !SLQ.state || !SLQ.state.widgetToken) return Promise.resolve(null);
    var returnTo = '/pages/skin-type-results';
    try {
      var u = new URL(String(resultsUrl || returnTo), window.location.origin);
      if (u.origin === window.location.origin) returnTo = u.pathname + u.search;
    } catch (e) { /* keep the default */ }
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    var timer = ctrl ? window.setTimeout(function () { try { ctrl.abort(); } catch (e2) { /* noop */ } }, 12000) : null;
    return fetch(base + '/widget/account/activate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + SLQ.state.widgetToken },
      body: JSON.stringify({ password: password, returnTo: returnTo, marketingConsent: !!state.marketingConsent }),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); })
      .then(function (res) {
        if (timer) window.clearTimeout(timer);
        if (res.ok && res.body && res.body.ok && typeof res.body.loginUrl === 'string' && /^https:\/\//.test(res.body.loginUrl)) {
          SLQ.track('gate_account_created');
          return res.body.loginUrl;
        }
        SLQ.track('gate_account_refused', { status: res.status, error: res.body && res.body.error });
        return null;
      })
      .catch(function (err) {
        if (timer) window.clearTimeout(timer);
        try { console.warn('[SLQ] account activation failed:', err && err.message); } catch (e3) { /* noop */ }
        SLQ.track('gate_account_failed');
        return null;
      });
  }

  function finishSuccess(body) {
    var skinType = body.skinType || null;
    var sensitivityLevel = ('sensitivityLevel' in body) ? body.sensitivityLevel : null;

    SLQ.state = {
      widgetToken: body.widgetToken || null,
      skinType: skinType,
      sensitivityLevel: sensitivityLevel,
      customerId: ('customerId' in body) ? body.customerId : null,
      regimenId: ('regimenId' in body) ? body.regimenId : null,
      quizId: body.quizId || null,
      submissionId: body.submissionId || null,
      email: state.email
    };
    ssSet('slq_result', JSON.stringify({
      widgetToken: SLQ.state.widgetToken,
      skinType: SLQ.state.skinType,
      sensitivityLevel: SLQ.state.sensitivityLevel
    }));

    // localStorage ALLOW-LIST ONLY (spec §3). The legacy re-arm flags and the
    // legacy routine cache key from the spec's forbid list are NEVER written.
    if (skinType) lsSet('customerSkinType', skinType);
    if (Array.isArray(body.sensitivityList)) {
      lsSet('customerSensitivityList', JSON.stringify(body.sensitivityList));
    } else if (sensitivityLevel) {
      // outcome carries a single real sensitivity code; store it in the
      // legacy JSON-array shape without inventing additional values
      lsSet('customerSensitivityList', JSON.stringify([sensitivityLevel]));
    }
    if (state.email) lsSet('customerEmail', state.email);
    if (state.affiliateCode) lsSet('affiliateCode', state.affiliateCode);

    // per-type theming from the shop metafield (never guessed)
    var info = SLQ.typeInfo(skinType);
    if (info && info.color) setTypeColors(info.color);

    SLQ.track('quiz_complete');
    // beacon: the results redirect follows immediately and must not lose it
    gaEvent('completed', { skin_type: skinType, sensitivity: sensitivityLevel, transport_type: 'beacon' });

    /* Separate results page (settings.results_url). The routine gets its own
       URL so it can be returned to later. The handoff rides sessionStorage —
       NOT the query string: widgetToken is a bearer credential and a URL leaks
       through Referer, history, and any analytics that records paths.

       A visitor who gave us an email ("saved" their results) also gets a
       namespaced localStorage copy, so coming back later lands them straight
       on their routine without logging in. Namespaced under slq* on purpose:
       `customerRoutine` and the re-arm flags are on the spec's forbid list. */
    var resultsUrl = SLQ.settings && SLQ.settings.resultsUrl;
    if (resultsUrl) {
      var handoff = {
        widgetToken: SLQ.state.widgetToken,
        skinType: SLQ.state.skinType,
        sensitivityLevel: SLQ.state.sensitivityLevel
      };
      try { sessionStorage.setItem('slqResultsHandoff', JSON.stringify(handoff)); } catch (e) { /* noop */ }
      if (state.email) {
        try {
          localStorage.setItem('slqSavedResults', JSON.stringify({
            widgetToken: handoff.widgetToken,
            skinType: handoff.skinType,
            sensitivityLevel: handoff.sensitivityLevel,
            savedAt: Date.now()
          }));
        } catch (e) { /* noop */ }
      }
      /* The gate's optional password: turn the store record this submission
         just created into a real account, then arrive on the results page
         LOGGED IN (Multipass; the handoff above rides sessionStorage through
         the hop, so the page still renders from the token). Any refusal or
         failure simply continues to the results as a guest — an account is a
         bonus on top of the results, never a condition for seeing them. */
      if (state.password && SLQ.state.widgetToken) {
        var pw = state.password;
        state.password = '';
        activateGateAccount(pw, resultsUrl).then(function (loginUrl) {
          /* snippets/quiz.liquid (legacy, still in the layout) stores the
             logged-out page path as checkout_url and, on the first logged-in
             page, redirects to it — for a quiz path that means the BARE
             results URL, which on the dev template drops ?view= and lands on
             the wrong results page. The Multipass return_to already carries
             the right destination, so clear the legacy hint before the hop. */
          if (loginUrl) { try { localStorage.removeItem('checkout_url'); } catch (e2) { /* noop */ } }
          window.location.href = loginUrl || resultsUrl;
        });
        return;
      }
      window.location.href = resultsUrl;
      return;
    }

    go('results');
    if (SLQ.results && typeof SLQ.results.show === 'function') {
      SLQ.results.show(SLQ.state);
    } else {
      // results file not initialized yet — it must consume SLQ.pendingResult on load
      SLQ.pendingResult = SLQ.state;
    }
  }

  // ------------------------------------------------------------------ landing

  function wireLanding() {
    $all('[data-slq-start]').forEach(function (btn) {
      btn.addEventListener('click', startQuiz);
    });

    var exitBtn = $('[data-slq-exit]');
    if (exitBtn) exitBtn.addEventListener('click', function () { go('landing'); });

    var copyBtn = $('[data-slq-hero-copy-btn]');
    var copyMobile = $('[data-slq-hero-copy-mobile]');
    var copyArrow = $('[data-slq-hero-copy-arrow]');
    if (copyBtn && copyMobile) {
      copyBtn.addEventListener('click', function () {
        var open = copyMobile.hasAttribute('hidden');
        copyMobile.toggleAttribute('hidden', !open);
        if (copyArrow) copyArrow.textContent = open ? '▴' : '▾';
      });
    }

    // Dr. Baumann's full intro. She prefers the long version; it is too long
    // for the hero, so it lives behind a Learn more toggle like the
    // per-question info button.
    var moreBtn = $('[data-slq-hero-more-btn]');
    var morePanel = $('[data-slq-hero-more]');
    var moreArrow = $('[data-slq-hero-more-arrow]');
    if (moreBtn && morePanel) {
      moreBtn.addEventListener('click', function () {
        var open = moreBtn.getAttribute('aria-expanded') === 'true';
        moreBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
        morePanel.toggleAttribute('hidden', open);
        if (moreArrow) moreArrow.textContent = open ? '▾' : '▴';
      });
    }

    // Explore-16 octagon grid: real colors from skinTypeInfo when present;
    // caption derives trait words from the code letters (AXES)
    var capCode = $('[data-slq-grid-cap-code]');
    var capText = $('[data-slq-grid-cap-text]');
    $all('[data-slq-type-tile]').forEach(function (tile) {
      var code = tile.getAttribute('data-slq-type-code');
      var info = SLQ.typeInfo(code);
      // Official octagon art carries its own color; the color fill is only a
      // fallback for a tile whose icon image is absent.
      if (info && info.color && !tile.querySelector('img')) tile.style.background = info.color;
      tile.addEventListener('click', function () {
        $all('[data-slq-type-tile]').forEach(function (t) { t.parentElement.style.borderColor = 'transparent'; });
        tile.parentElement.style.borderColor = '#1b1d3a';
        if (capCode) capCode.textContent = code;
        if (capText) {
          capText.textContent = code.split('').map(function (l) { return AXES[l] || l; }).join(' · ');
        }
      });
    });
  }

  // --------------------------------------------------------------------- init

  // Content review: Dr. Baumann reviews question copy on dev before it ships to
  // production, but the quiz page is hardwired to the production API. Rather
  // than repoint the whole theme (a published theme pointing at dev would send
  // real patients to the dev database), ?slqEnv= swaps the API base for that
  // one visit only. Allowlisted on purpose — never take a host from the URL.
  var ENV_BASES = {
    dev: 'https://dev.skintype.pro',
    staging: 'https://staging.skintype.pro',
    prod: 'https://app.skintype.pro'
  };

  function envOverrideBase() {
    try {
      var v = new URLSearchParams(window.location.search).get('slqEnv');
      return (v && ENV_BASES[v.toLowerCase()]) || '';
    } catch (e) { return ''; }
  }

  function readSettings() {
    var envBase = envOverrideBase();
    SLQ.settings = {
      nextBase: envBase || root.getAttribute('data-next-base') || '',
      ec2Base: root.getAttribute('data-ec2-base') || '',
      proxyBase: root.getAttribute('data-proxy-base') || '/apps/quiz',
      useProxySubmit: root.getAttribute('data-use-proxy-submit') === 'true',
      turnstileSiteKey: root.getAttribute('data-turnstile-site-key') || '',
      // Blank = render results inline on this page (original behaviour).
      // Set = redirect to that URL after scoring, so the routine has its own page.
      resultsUrl: root.getAttribute('data-results-url') || '',
      // GA4 event names (theme editor > quiz section > Google Analytics events).
      // Blank = that event is off. See gaEvent() below.
      ga: {
        category: root.getAttribute('data-ga-category') || 'Custom Event',
        landed: root.getAttribute('data-ga-landed') || '',
        started: root.getAttribute('data-ga-started') || '',
        questionLoaded: root.getAttribute('data-ga-question-loaded') || '',
        questionAnswered: root.getAttribute('data-ga-question-answered') || '',
        completed: root.getAttribute('data-ga-completed') || ''
      }
    };
    if (envBase) {
      try {
        console.warn('[SLQ] API base overridden by ?slqEnv -> ' + envBase);
        var b = document.createElement('div');
        b.setAttribute('data-slq-env-banner', '');
        b.textContent = 'Preview: questions loaded from ' + envBase.replace('https://', '');
        b.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;'
          + 'background:#2F3572;color:#fff;font:600 12px/1.5 system-ui,sans-serif;'
          + 'text-align:center;padding:6px 12px;letter-spacing:.02em';
        (document.body || document.documentElement).appendChild(b);
      } catch (e) { /* banner is advisory only */ }
    }
  }

  function resolveAffiliateCode() {
    // Legacy parity (assets/quiz.js init:346-363 + getURLParameter:536-554):
    // ?code=AFFILIATE-PROVIDERID splits on the FIRST hyphen — part 0 is the
    // affiliate code, part 1 (when present) a providerId; the raw value is
    // kept as fullCodeParam. All three persist exactly like the legacy flow
    // (case preserved — downstream comparisons like IDEAL are case-sensitive
    // in production). Stored codes win when no URL code is present.
    try {
      var rawCode = new URLSearchParams(window.location.search).get('code');
      if (rawCode && rawCode.trim()) {
        rawCode = rawCode.trim();
        var parts = rawCode.split('-');
        var code = parts[0];
        var providerId = parts[1];
        try {
          localStorage.setItem('fullCodeParam', rawCode);
          localStorage.setItem('affiliateCode', code);
          sessionStorage.setItem('affiliateCode', code);
          if (providerId) {
            localStorage.setItem('providerId', providerId);
            sessionStorage.setItem('providerId', providerId);
          }
        } catch (e) { /* noop */ }
        return code;
      }
    } catch (e) { /* noop */ }
    var stored = ssGet('affiliateCode') || lsGet('affiliateCode');
    return stored && stored.trim() ? stored.trim() : null;
  }

  // ---------------------------------------------- affiliate question logic
  // Ports of assets/quiz.js's Octane-DOM customizations, applied to our own
  // renderer instead of MutationObservers.

  // quiz.js:65 — known-affiliate list; when the visitor arrived with a known
  // code the Practice Code input is hidden (they never edit it). Fail-open.
  var knownAffiliateCodes = null;
  try {
    fetch('https://cdn.shopify.com/s/files/1/0740/5984/1838/files/affiliateNames.json?v=1692642219')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        knownAffiliateCodes = (d || []).map(function (a) { return a.affiliateCode; });
      })
      .catch(function () { /* fail-open: input stays visible */ });
  } catch (e) { /* noop */ }

  function effectiveAffiliateCodeUpper() {
    var q1 = state.answers && state.answers.q1;
    var typed = (Array.isArray(q1) ? q1[0] : q1 || '').trim();
    var code = typed || state.affiliateCode || 'STSMD';
    return String(code).toUpperCase();
  }

  // quiz.js:123-150 — GETREFRESHED hides medical/body options on the concerns
  // question and the skin-issues question, and reveals aesthetic-only ones.
  // Questions are identified by option TEXT (same as legacy), never by id.
  var AFFILIATE_OPTION_RULES = {
    concerns: {
      identifiers: ['Acne Scars', 'Broken blood vessels', 'Cellulite'],
      hideForRefreshed: [
        'Broken blood vessels on face or body',
        'Leg veins',
        'Scalp redness or flaking',
        'Toenail or foot fungus (scaling on feet)'
      ],
      showForRefreshed: [
        'Hollow Under eyes',
        'Hip Dips',
        'Jaw Contouring',
        'Droopy/Bulbous Nose Tip',
        'Nasal Hump'
      ]
    },
    skinIssues: {
      identifiers: ['Dandruff or itching scalp', 'Dry Lips', 'Dry skin'],
      hideForRefreshed: [
        'Dandruff or itching scalp',
        'Weak nails',
        'Psoriasis',
        'Eczema'
      ],
      showForRefreshed: []
    }
  };

  function applyAffiliateOptionRules(body) {
    var isRefreshed = effectiveAffiliateCodeUpper() === 'GETREFRESHED';
    var buttons = body.querySelectorAll('.slq-opt');
    if (!buttons.length) return;
    var texts = [];
    buttons.forEach(function (b) { texts.push(b.textContent); });
    var config = null;
    Object.keys(AFFILIATE_OPTION_RULES).forEach(function (k) {
      var c = AFFILIATE_OPTION_RULES[k];
      var matches = c.identifiers.some(function (id) {
        return texts.some(function (t) { return t.indexOf(id) !== -1; });
      });
      if (matches && !config) config = c;
    });
    if (!config) return;
    buttons.forEach(function (b) {
      var t = b.textContent;
      var inHide = config.hideForRefreshed.some(function (x) { return t.indexOf(x) !== -1; });
      var inShow = config.showForRefreshed.some(function (x) { return t.indexOf(x) !== -1; });
      // Restoring visibility must put back the option's OWN display mode.
      // Setting '' clears the inline `display:flex` these buttons are built
      // with (renderQuestion), dropping them to `display:block` — the letter
      // badge is a flex child with min-width:28px and no width, so it then
      // stretches to fill the whole row as a flat ellipse and pushes the label
      // onto a second line. This hit every NON-GETREFRESHED visitor, since the
      // else-branch below reveals the hideForRefreshed options.
      if (isRefreshed) {
        if (inHide) b.style.display = 'none';
        else if (inShow) b.style.display = OPT_DISPLAY;
      } else {
        if (inHide) b.style.display = OPT_DISPLAY;
        else if (inShow) b.style.display = 'none';
      }
    });
    relabelVisibleOptions(body);
  }

  /* Letters are assigned at render time from the option's index in the FULL
     list, so any option hidden above leaves a gap in the sequence — "General
     concerns" showed … T, U, X with V and W missing. Re-letter what the
     visitor can actually see, after the show/hide pass. */
  function relabelVisibleOptions(body) {
    var visible = [];
    body.querySelectorAll('.slq-opt').forEach(function (b) {
      if (b.style.display !== 'none') visible.push(b);
    });
    visible.forEach(function (b, i) {
      var key = b.firstElementChild;
      if (key) key.textContent = optionLetter(i);
    });
  }

  function applyQuestionCustomizations(q, body) {
    var codeUpper = effectiveAffiliateCodeUpper();

    // Practice code entry (q1): hide the input for known-affiliate arrivals
    // (quiz.js:62-78) — the code is already applied; the visitor just
    // continues. Shown as a confirmation note instead of a bare hidden field.
    if (q.id === 'q1') state.q1Locked = false;
    if (q.id === 'q1' && state.affiliateCode && knownAffiliateCodes &&
        knownAffiliateCodes.indexOf(state.affiliateCode) !== -1) {
      var inp = body.querySelector('input, textarea');
      if (inp) inp.style.display = 'none';
      // The box is hidden, so the visitor has no way to type or clear a code:
      // an empty draft here means "nothing to edit", NOT "cleared on purpose".
      // Without this lock Continue blanked state.affiliateCode and every
      // arrival with a recognised practice code submitted as STSMD.
      state.q1Locked = true;
      var note = document.createElement('div');
      note.style.cssText = 'padding:14px 16px;border:1.5px solid var(--slq-type-tint);background:var(--slq-type-tint);border-radius:14px;font-size:15px;color:#26284a';
      note.textContent = 'Practice code applied: ' + state.affiliateCode;
      body.appendChild(note);
    }

    // (The legacy IDEAL customizations — birthday copy, required zip, the
    // IDEALROUTINE discount — were removed 2026-08-06: Ideal Image is no
    // longer a partner. RADIANCE's email rule stays inherently satisfied by
    // the always-required email gate.)

    // Unknown-but-present codes: prefill the practice-code input like the
    // legacy flow prefilled Octane's (quiz.js setQuizIdFromParams:42-45).
    if (q.id === 'q1' && state.affiliateCode && (state.draft === '' || state.draft == null)) {
      var q1inp = body.querySelector('input, textarea');
      if (q1inp && q1inp.style.display !== 'none') {
        state.draft = state.affiliateCode;
        q1inp.value = state.affiliateCode;
      }
    }

    // NO identity prefill from customerQuizAnswers (q17 name / q18 birth month).
    // That key is written ONLY by the legacy flow and is not on the spec §3
    // allow-list, so this widget can read it but never refresh it — it decays
    // into a permanently stale identity. On a shared or kiosk browser it made
    // the quiz prefill a PREVIOUS visitor's name and birth date. The email box
    // still prefills, but from `customerEmail`, which is allow-listed and this
    // widget rewrites on every successful submit (see finishSuccess).

    applyAffiliateOptionRules(body);
  }

  function init() {
    root = document.querySelector('[data-slq-root]');
    if (!root) return;
    readSettings();
    setTypeColors(NEUTRAL_HEX);
    state.affiliateCode = resolveAffiliateCode();

    wireLanding();
    gaEvent('landed');

    // The header/mobile menu's "Retake the Quiz" link is /pages/skin-type-quiz?retake
    // (legacy contract). Honour it: drop any checkpointed answers so the shopper
    // starts clean instead of resuming a half-finished run from this tab.
    var wantsRetake = false;
    try { wantsRetake = new URLSearchParams(window.location.search).has('retake'); } catch (e) { /* noop */ }
    if (wantsRetake) clearProgress();

    // Mid-quiz reload: saved progress resumes the quiz directly at the same
    // question (restoreProgress applies the answers once questions arrive).
    if (hasSavedProgress()) startQuiz();

    var cont = $('[data-slq-continue]');
    if (cont) cont.addEventListener('click', onContinue);
    var skipBtn = $('[data-slq-skip]');
    if (skipBtn) skipBtn.addEventListener('click', onSkip);
    var back = $('[data-slq-back]');
    if (back) back.addEventListener('click', goBack);
    var retry = $('[data-slq-quiz-retry]');
    if (retry) retry.addEventListener('click', function () {
      state.questionsStatus = 'idle';
      fetchQuestions();
    });

    var gateForm = $('[data-slq-gate-form]');
    if (gateForm) gateForm.addEventListener('submit', onGateSubmit);
    var gateEmailInput = $('[data-slq-gate-email]');
    if (gateEmailInput) {
      gateEmailInput.addEventListener('input', function () { scheduleGateEmailCheck(false); });
      gateEmailInput.addEventListener('blur', function () { scheduleGateEmailCheck(true); });
    }
    var gateHint = $('[data-slq-gate-hint]');
    if (gateHint) gateHint.addEventListener('click', function (ev) {
      var t = ev.target;
      if (t && t.hasAttribute && t.hasAttribute('data-slq-gate-login')) onGateLoginFirst();
    });
    var gateSkip = $('[data-slq-gate-skip]');
    if (gateSkip) gateSkip.addEventListener('click', function () { proceedFromGate(true); });
    var gateBack = $('[data-slq-gate-back]');
    if (gateBack) gateBack.addEventListener('click', function () { closeGate(); });
    var gateBackdrop = $('[data-slq-gate-backdrop]');
    if (gateBackdrop) gateBackdrop.addEventListener('click', function () { closeGate(); });

    var submitRetry = $('[data-slq-submit-retry]');
    if (submitRetry) submitRetry.addEventListener('click', function () {
      // a fresh attempt-set: NEW submissionId, freshly built (byte-identical
      // within its own attempt-set); answers + email are preserved — plus a
      // freshly minted Turnstile token, without which the retry is guaranteed
      // to 403 on a single-use-token duplicate.
      retryWithFreshToken();
    });

    // prefetch questions so Start is instant; quiz view shows honest
    // skeleton/error states if this is still pending or failed
    fetchQuestions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
